/**
 * The wire contract.
 *
 * These types mirror `backend/app/api/schemas.py` field for field. When the
 * backend changes, this file changes — it is not a convenience layer and it
 * does not reshape anything.
 *
 * Three conventions carried over from the backend, all of them load-bearing:
 *
 * **Money is a string.** `Decimal` has no JSON representation and a float
 * round-trip loses precision, so prices, quantities and balances arrive as
 * exact base-10 strings and are typed `DecimalString`. They become numbers
 * in exactly one place: `lib/format.ts`, at the moment of drawing. Nothing
 * in this app does arithmetic on money.
 *
 * **Analytics values are floats.** Equity curves, indicator series and
 * metrics go straight to a chart, so they are `number`.
 *
 * **Undefined is `null`, never `0`.** A profit factor of zero and an
 * undefined profit factor mean opposite things. Every optional numeric is
 * `| null` and the UI must render a placeholder, not a zero.
 *
 * Timestamps: `time`/`created_at`/… are ISO-8601 UTC strings. Chart payloads
 * additionally carry `t`, epoch **seconds** of a bar's `open_time` — chart
 * libraries key bars by that integer, and a marker whose time is derived any
 * other way lands on the wrong bar or on none at all.
 */

/** An exact decimal, as it crosses the wire. Never do maths on this. */
export type DecimalString = string;

/** ISO-8601 UTC instant, e.g. `2026-03-14T08:30:00Z`. */
export type IsoDateTime = string;

/** Epoch **seconds**. What KLineChart keys bars and markers by. */
export type EpochSeconds = number;

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

export type JsonObject = Record<string, unknown>;

// ══════════════════════════════════════════════════════════════════════
//  Enums — mirroring app.core.models / app.config / app.db.models
// ══════════════════════════════════════════════════════════════════════

/**
 * Every timeframe the platform offers, finest first.
 *
 * Must stay identical to `app.core.models.Timeframe`. `2h` was added to the
 * backend enum and downloaded for all 50 symbols, but was missing here — so
 * the store held 2h bars for every pair that no picker in the UI could ask
 * for. A timeframe list that is a subset of what exists is exactly the M4
 * failure ("every pair, every timeframe") with the evidence hidden, because
 * the option is absent rather than empty.
 */
export const TIMEFRAMES = [
  '1m',
  '5m',
  '15m',
  '30m',
  '1h',
  '2h',
  '4h',
  '12h',
  '1d',
  '1w',
] as const;
export type Timeframe = (typeof TIMEFRAMES)[number];

/** Seconds per bar. Used for range maths and for KLineChart's `Period`. */
export const TIMEFRAME_SECONDS: Record<Timeframe, number> = {
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '30m': 1800,
  '1h': 3600,
  '2h': 7200,
  '4h': 14400,
  '12h': 43200,
  '1d': 86400,
  '1w': 604800,
};

/**
 * Downsample target for every chart request. **Candles and indicators must
 * use the same value.**
 *
 * The server buckets a series into at most `max_points` bars and labels each
 * bucket with its first bar's timestamp. That bucketing depends on
 * `max_points`, so two endpoints asking for different values describe
 * different bars — and a chart can only draw an indicator point against a
 * candle whose timestamp matches exactly.
 *
 * This existed as `3000` in Markets.tsx and `4000` in IndicatorPicker.tsx.
 * Indicators drew nothing on 1m through 4h. It looked like "some timeframes
 * are broken" because on 1d the coarser indicator grid happened to be a
 * superset of the candle grid, so every candle still found a match.
 *
 * One constant, imported by both. Do not inline a number at a call site.
 */
export const CHART_MAX_POINTS = 3000;

/**
 * How many bars of history a chart opens with, at native resolution.
 *
 * Both market endpoints default to `limit=200000` — every bar ever stored.
 * The server then buckets that down to `max_points`, so asking for a "1h
 * chart" returned 75,419 hourly bars compressed 26:1 and every candle on
 * screen was 26 hours wide. Measured across the board: 30m rendered 1.1-day
 * bars, 1h 1.1-day, 2h 1.1-day, 4h 1.2-day, 1d 2.0-day. Every intraday
 * timeframe was, in effect, the daily chart — which is precisely how it
 * looked, and why no indicator could respond to a timeframe change.
 *
 * Bounding the source window instead keeps `downsample_factor` at 1, so one
 * bar on screen is one bar in the store. A chart that silently shows
 * composite bars under a "1h" label is worse than a chart that shows
 * nothing, because it is believable.
 */
export const CHART_WINDOW_BARS = 1500;

export type Side = 'buy' | 'sell';

export type OrderType =
  | 'market'
  | 'limit'
  | 'stop_loss'
  | 'stop_loss_limit'
  | 'take_profit'
  | 'take_profit_limit';

export type OrderStatus =
  | 'pending'
  | 'open'
  | 'partially_filled'
  | 'filled'
  | 'cancelled'
  | 'rejected'
  | 'expired';

/** TESTNET = fake money. PAPER = real data, simulated fills. LIVE = real. */
export type Environment = 'testnet' | 'paper' | 'live';

export type Venue = 'binance' | 'kraken';

export type StrategyKind = 'code' | 'rules';

/**
 * How a run ended. [F7]
 *
 * `halted` is not `completed`, for the same reason `DeploymentStatus` below
 * separates them: a kill switch stopped the run, the bars after it were never
 * evaluated, and every metric on the run describes only the part that was.
 * The owner's standard configuration halts on 2018-06-22 having covered 5.5%
 * of a 2018→2026 request — badged `completed`, it read as eight years.
 *
 * Anything rendering this must not fall back to "completed-ish": a halted run
 * is closer to a failed one for the purpose of trusting its numbers.
 */
export type BacktestStatus = 'running' | 'completed' | 'halted' | 'failed';

/**
 * HALTED is not STOPPED. STOPPED is a decision; HALTED is the kill-switch,
 * and re-arming from it requires an explicit audited action. A UI that
 * renders them the same way is a UI that hides a breach.
 */
export type DeploymentStatus = 'running' | 'paused' | 'stopped' | 'halted' | 'closed';
export type ExecutionState =
  | 'detached'
  | 'pending'
  | 'attaching'
  | 'attached'
  | 'stale'
  | 'failed'
  | 'blocked_live'
  | 'legacy_review';

export type AlertSeverity = 'info' | 'warning' | 'critical';

export type LifecycleStage =
  | 'draft'
  | 'tested'
  | 'promoted'
  | 'paper'
  | 'live'
  | 'retired';

// ══════════════════════════════════════════════════════════════════════
//  Envelopes
// ══════════════════════════════════════════════════════════════════════

export interface ErrorBody {
  /** Machine-readable class, e.g. `not_found`, `limit_breached`. */
  code: string;
  /** Written for the person reading it. */
  message: string;
  /** Structured detail — which limit, which symbol, which gate. */
  context: JsonObject;
  /** Correlates this response with the server log. Quote it. */
  request_id: string;
}

export interface ErrorResponse {
  error: ErrorBody;
}

/** A slice of a larger collection. `total` is the count before limit/offset. */
export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

// ══════════════════════════════════════════════════════════════════════
//  Jobs
// ══════════════════════════════════════════════════════════════════════

/** Long work returns one of these with 202; progress streams on `jobs`. */
export interface JobOut {
  job_id: string;
  kind: string;
  status: string;
  /** 0–100. */
  progress: number;
  stage: string;
  message: string;
  meta: JsonObject;
  error: string | null;
  created_at: IsoDateTime;
  started_at: IsoDateTime | null;
  finished_at: IsoDateTime | null;
  /**
   * The finished job's payload. `Job.snapshot()` deliberately omits it — a
   * result can be megabytes — so the list and cancel endpoints never carry
   * it. One endpoint adds it back on top of the snapshot:
   * `GET /api/backtest/jobs/{job_id}` (`routes_backtest.py`). It used to be
   * two — `/api/validation/jobs/{job_id}` went with the Validate tab. Hence
   * optional: absent on every other job response, and `null` until the job
   * succeeds.
   */
  result?: JsonObject | null;
}

// ══════════════════════════════════════════════════════════════════════
//  Market data
// ══════════════════════════════════════════════════════════════════════

export interface SymbolSummaryOut {
  symbol: string;
  base_asset: string;
  quote_asset: string;
  /**
   * True when the venue accepts orders, false when it has halted the pair,
   * **null when we do not know** — no venue snapshot yet, or one too old to
   * assert from.
   *
   * The third state is load-bearing (M10). It used to be a plain boolean, so
   * "we never reached Binance" and "Binance has halted this market" were the
   * same value, and every row in the browser rendered a warning about a halt
   * that had not happened. Render `false`; render nothing for `null`.
   */
  tradeable: boolean | null;
  /** When the venue snapshot behind `tradeable` was taken. */
  venue_as_of?: IsoDateTime | null;
  /** Timeframes with local history — the ones you can actually backtest. */
  stored_timeframes: Timeframe[];
  /**
   * Decimal places the venue prices this pair to (its tick size as digits), or
   * `null` when no venue snapshot is cached. Served on the row so the list can
   * print each price at the SAME precision as the chart header (M5) instead of
   * a magnitude bucket that overstates the tick.
   */
  price_precision: number | null;
  stored_bars: number;
  stored_start: IsoDateTime | null;
  stored_end: IsoDateTime | null;
  bytes_on_disk: number;
}

/**
 * One pair's current price. **Display only — never an order input.**
 *
 * `price` is a STRING, and that is not an accident: the backend models it as
 * `Decimal` (E1) and serialising it as a JSON number would round it on the
 * way out. Parse it at the point of formatting, not on the way in.
 */
export interface PriceRow {
  symbol: string;
  price: string;
  /** Change over the rolling 24h window, in percent. */
  change_pct: number | null;
}

export interface PricesOut {
  /** When the venue snapshot was taken. `null` if none has ever succeeded. */
  as_of: IsoDateTime | null;
  /**
   * The snapshot is older than the platform will call current.
   *
   * **Render nothing rather than a number when this is set.** A price that
   * has stopped updating is indistinguishable on screen from a market that
   * has stopped moving, and the difference costs money. The server already
   * withholds the rows — this flag is what lets the UI say *why* the column
   * is empty instead of looking broken.
   */
  stale: boolean;
  /** Why the last refresh failed, if it did. Prices may still be present. */
  error: string | null;
  items: PriceRow[];
}

export interface SymbolSeriesOut {
  timeframe: Timeframe;
  rows: number;
  start: IsoDateTime | null;
  end: IsoDateTime | null;
  years: number[];
  bytes_on_disk: number;
}

/**
 * Trading rules plus stored history.
 *
 * Tick size, lot step and min notional are here because every price and
 * quantity the platform sends is rounded through them. A UI that hides them
 * cannot explain why an order was refused.
 */
export interface SymbolDetailOut {
  symbol: string;
  base_asset: string;
  quote_asset: string;
  tradeable: boolean;
  price_tick: DecimalString | null;
  quantity_step: DecimalString | null;
  min_quantity: DecimalString | null;
  min_notional: DecimalString | null;
  price_precision: number | null;
  quantity_precision: number | null;
  /** Set when venue metadata could not be fetched; stored fields still valid. */
  exchange_error: string | null;
  series: SymbolSeriesOut[];
}

/**
 * One bar. `t` is epoch seconds of `open_time` — key markers off this.
 *
 * `time` is the same instant spelled again as ISO text. The server includes
 * it in `format=objects`; bars rebuilt from `format=columns` omit it, since
 * nothing in this app reads it and 3,000 `Date` allocations per fetch is a
 * measurable cost for a field that exists only to be redundant. Derive it
 * from `t` if you ever need it, and derive it in one place.
 */
export interface CandleBar {
  t: EpochSeconds;
  time?: IsoDateTime;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

/** Everything a candle response says that is not a bar. */
export interface CandleMeta {
  symbol: string;
  timeframe: Timeframe;
  returned: number;
  /** Stored bars covered by this response, before downsampling. */
  source_bars: number;
  /** Stored bars per returned bar. 1 = untouched. */
  downsample_factor: number;
  /** True when bars are aggregates, not stored bars. Never trade off these. */
  aggregated: boolean;
  start: IsoDateTime | null;
  end: IsoDateTime | null;
  /**
   * True when older bars exist that this window did not return.
   *
   * The pan signal: ask again with `end` set to the oldest bar you hold.
   */
  truncated: boolean;
  /** True when these bars were folded from a finer stored timeframe. */
  derived?: boolean;
  source_timeframe?: Timeframe | null;
  /** 'stored', or 'derived from 1h'. Show it. */
  provenance?: string;
}

export interface CandleResponse extends CandleMeta {
  bars: CandleBar[];
}

/**
 * `format=columns`: the same bars as six parallel arrays.
 *
 * Measured on 3,000 BTCUSDT 1h bars: 385KB as `bars` objects, 172KB as
 * columns, 53KB as columns over gzip. Two thirds of the object payload is
 * the seven key names repeated per bar plus an ISO `time` string that
 * duplicates `t` exactly. Arrays also parse to typed data the chart can
 * walk without touching 3,000 object headers.
 *
 * Index i of every array describes the same bar. They are always the same
 * length; that is the contract.
 */
export interface CandleColumns extends CandleMeta {
  /** Epoch seconds of each bar's open_time. The only timestamp on the wire. */
  t: EpochSeconds[];
  o: number[];
  h: number[];
  l: number[];
  c: number[];
  v: number[];
  /**
   * True when this window runs to the newest stored bar.
   *
   * False means the caller closed the range in the past, which is finished
   * history — safe to cache for as long as the tab lives, because a closed
   * range does not acquire new bars.
   */
  complete: boolean;
}

/**
 * Columns plus the object form, which the hooks materialise once per fetch.
 *
 * Both shapes are here because they answer different questions: chart code
 * that walks bars wants `bars`, and anything doing arithmetic over a series
 * wants the arrays without paying for 3,000 property lookups. They are
 * built from the same numbers in one pass, so they cannot disagree.
 */
export interface CandleSeries extends CandleColumns {
  bars: CandleBar[];
}

export interface CandleQuery {
  symbol: string;
  timeframe: Timeframe;
  start?: IsoDateTime;
  end?: IsoDateTime;
  /** Ceiling on stored bars loaded. 10–2,000,000. */
  limit?: number;
  /** Downsample target. 10–50,000. */
  max_points?: number;
  /**
   * Wire shape. The hooks send `columns` and rebuild `bars` client-side;
   * `objects` is the server default and exists for anything not using them.
   */
  format?: 'objects' | 'columns';
}

export interface SymbolQuery {
  q?: string;
  quote?: string;
  timeframe?: Timeframe;
  source?: 'stored' | 'exchange' | 'all';
  /**
   * Restrict the answer to the tradeable universe — exactly the 50 pairs in
   * `data/universe.json` (M1). The Markets browser pins this true, so no
   * control in the UI can widen the list; `source=all` without it measured
   * 1,382 rows, 350 of the first 400 with no local history.
   */
  universe?: boolean;
  sort?: 'symbol' | 'bars' | 'start' | 'end' | 'size';
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

// ── Indicators ────────────────────────────────────────────────────────
//
// Every value here is computed in Python. The frontend never calculates an
// indicator, ever. Two implementations of RSI silently disagree, and then
// the chart shows a signal the bot never traded.

export interface IndicatorParamSpec {
  name: string;
  type: string;
  default: JsonValue;
  min?: number | null;
  max?: number | null;
  step?: number | null;
  options?: JsonValue[] | null;
  description?: string;
  [key: string]: unknown;
}

export interface IndicatorOutputSpec {
  name: string;
  display_name?: string;
  pane?: string;
  color?: string;
  line_width?: number;
  series_type?: string;
  [key: string]: unknown;
}

export interface IndicatorSpec {
  key: string;
  display_name: string;
  category: string;
  description: string;
  pane: string;
  is_pattern: boolean;
  parameters: IndicatorParamSpec[];
  outputs: IndicatorOutputSpec[];
  [key: string]: unknown;
}

export interface IndicatorCatalog {
  categories: string[];
  pattern_keys: string[];
  indicators: IndicatorSpec[];
  count: number;
}

/** One indicator to compute, by registry key. */
export interface IndicatorRequestIn {
  key: string;
  params?: JsonObject;
  /** Override the column prefix so two EMAs can be `fast` and `slow`. */
  prefix?: string | null;
}

export interface IndicatorComputeRequest {
  symbol: string;
  timeframe: Timeframe;
  source?: 'store' | 'live';
  indicators: IndicatorRequestIn[];
  start?: IsoDateTime | null;
  end?: IsoDateTime | null;
  limit?: number;
  /** Indicators are computed over the FULL range, then sampled. */
  max_points?: number;
}

/** One output column. `null` is NaN — JSON has no NaN, and a chart handed
 *  one silently stops drawing. */
export interface IndicatorSeriesOut {
  column: string;
  key: string;
  output: string;
  display_name: string;
  pane: string;
  color: string;
  line_width: number;
  series_type: string;
  values: Array<number | null>;
}

export interface IndicatorResponse {
  symbol: string;
  timeframe: Timeframe;
  source?: 'store' | 'live';
  /** Epoch seconds, aligned index-for-index with every `values` array. */
  times: EpochSeconds[];
  series: IndicatorSeriesOut[];
  /** Leading bars per indicator that are warmup residue, not real values. */
  warmup_bars: Record<string, number>;
  source_bars: number;
  downsample_factor: number;
}

export interface DownloadRequest {
  symbol: string;
  timeframe: Timeframe;
  start: IsoDateTime;
  end?: IsoDateTime | null;
  /** Re-download months already recorded complete. Bandwidth, not risk. */
  force?: boolean;
}

// ══════════════════════════════════════════════════════════════════════
//  Backtests
// ══════════════════════════════════════════════════════════════════════

/**
 * Fees and slippage — the assumptions that decide whether an edge exists.
 * Exposed rather than hidden: a backtest run at a fee rate you do not
 * actually pay is a work of fiction.
 */
export interface CostModelIn {
  maker_fee_pct?: DecimalString | number;
  taker_fee_pct?: DecimalString | number;
  market_slippage_bps?: DecimalString | number;
  stop_slippage_bps?: DecimalString | number;
  stop_slippage_atr_multiple?: DecimalString | number;
}

/** Risk-derived position sizing. Size is never chosen by hand. */
export interface SizerIn {
  risk_per_trade_pct?: DecimalString | number;
  max_position_pct_of_equity?: DecimalString | number;
  quantity_step?: DecimalString | number;
  min_quantity?: DecimalString | number;
  min_notional?: DecimalString | number;
}

/** Account-level breakers, simulated so the report shows the halts. */
export interface KillSwitchIn {
  enabled?: boolean;
  max_daily_loss_pct?: DecimalString | number;
  max_drawdown_pct?: DecimalString | number;
}

/** Exactly one of the three source fields may be set. */
export interface StrategySourceIn {
  strategy_def_id?: number | null;
  builtin?: string | null;
  /** `app.module:ClassName`. Restricted to the `app.` package. */
  code_ref?: string | null;
  params?: JsonObject;
}

export interface BacktestRequest {
  strategy: StrategySourceIn;
  symbol: string;
  timeframe: Timeframe;
  start?: IsoDateTime | null;
  end?: IsoDateTime | null;
  initial_cash?: DecimalString | number;
  label?: string;
  /**
   * Whether each trade is sized off current equity or off starting capital.
   * [F2]
   *
   * `compound` is what an account actually does, and is the default — but it
   * flatters a good early run, because wins that happened to land first scale
   * up every trade after them. `fixed` weights every trade equally, so
   * expectancy is evidence about the strategy rather than about the order its
   * results arrived in.
   */
  capital_mode?: CapitalMode;
  /**
   * Margin multiple, 1–8. [F1]
   *
   * It does **not** raise risk per trade. Risk-based sizing asks for
   * `equity × risk% / stop distance`, in which leverage does not appear, so 4%
   * risk loses 4% at the stop at 1× and 4% at 8×. What it changes is how often
   * that ask gets **clipped**: 41.1% of the owner's 3,605 stored trades were
   * sized below the configured risk because the position cost more cash than
   * the account held. Leverage does not raise risk above the setting, it stops
   * risk being cut below it.
   */
  leverage?: number;
  costs?: CostModelIn;
  sizer?: SizerIn;
  kill_switch?: KillSwitchIn;
  /**
   * `next_open` is the honest default: you cannot fill on a close you have
   * only just observed. `current_close` exists to compare against tools
   * that do it silently; its results are not tradeable.
   */
  fill_timing?: 'next_open' | 'current_close';
  min_stop_distance_fee_multiple?: DecimalString | number;
  allow_shorts?: boolean;
  max_bars?: number;
  persist?: boolean;
  /** Append a registry variant only after this persisted run succeeds. */
  register_variant?: boolean;
}

/**
 * One row of the run registry. Costs and the data range travel with the
 * metrics on purpose: two runs with different fee assumptions are not
 * comparable, and the UI needs enough here to say so out loud.
 */
export interface BacktestRunSummary {
  run_id: number;
  label: string;
  strategy_name: string;
  strategy_version: number;
  symbol: string;
  timeframe: Timeframe;
  /**
   * The range that was **requested**, not the range that was evaluated. [F7]
   *
   * They are the same thing only on a run that reached its last bar. A halted
   * run keeps its requested range here — [B4] re-runs from it and the run
   * fingerprint is built from it — and reports where it actually stopped in
   * `halted_at` / `evaluated_end`. Anything printing a date range on screen
   * must use `evaluated_end`, never `period_end`.
   */
  period_start: IsoDateTime;
  period_end: IsoDateTime;
  status: BacktestStatus;

  /** Why a kill switch ended this run early. Null when it ran to the end. */
  halted_reason: string | null;
  /** Last bar a halted run evaluated. Null when it ran to the end. */
  halted_at: IsoDateTime | null;
  /** `halted_at ?? period_end` — the last bar actually reached. Computed by the backend. */
  evaluated_end: IsoDateTime;
  /**
   * Percent of the requested period this run actually evaluated. 100 for a
   * run that finished; 5.46 for the owner's run 41. Every metric on the run
   * is computed over this fraction and has to be read as such.
   */
  coverage_pct: number;

  params: JsonObject;
  fee_rate: DecimalString;
  slippage_bps: DecimalString;
  initial_capital: DecimalString;
  final_equity: DecimalString | null;
  trade_count: number;
  metrics: BacktestMetrics;
  error: string | null;
  created_at: IsoDateTime;
  completed_at: IsoDateTime | null;

  /**
   * When the owner put this run out of the default registry view, or null.
   *
   * Archiving hides a run; it never deletes one. The row, its trades and its
   * equity curve are untouched — comparing today's tweak against March's run
   * is the only way to tell tuning from curve-fitting.
   */
  archived_at: IsoDateTime | null;
  /** `archived_at !== null`, computed by the backend so there is one fact. */
  archived: boolean;
}

/**
 * Headline metrics. Loosely typed on purpose — the backend owns which keys
 * exist, and a frontend that hardcodes the list silently drops new ones.
 * The named fields are the ones the UI is entitled to rely on.
 */
export interface BacktestMetrics {
  total_return_pct?: number | null;
  cagr_pct?: number | null;
  max_drawdown_pct?: number | null;
  sharpe?: number | null;
  sortino?: number | null;
  calmar?: number | null;
  profit_factor?: number | null;
  win_rate_pct?: number | null;
  expectancy_r?: number | null;
  total_trades?: number | null;
  fee_drag_pct?: number | null;
  detail?: JsonObject;
  [key: string]: unknown;
}

export interface BacktestRunDetail extends BacktestRunSummary {
  equity_curve: JsonObject[];
  /**
   * Where `equity_curve` came from. [E6]
   *
   * Curves are no longer persisted per bar — that was 543 MB of a 685 MB
   * database — so the backend rebuilds them from the run's trades and the
   * candle store on demand. `stored` is the engine's own output (a legacy
   * run, or one deliberately retained); `rebuilt` is faithful to the shape
   * of the curve but not to the cent; `unavailable` means the candles for
   * this range are gone and `equity_curve` is empty.
   *
   * Anything that draws this series must caption `rebuilt` as rebuilt and
   * must render `unavailable` as an empty state, never as a flat line.
   */
  equity_source?: 'stored' | 'rebuilt' | 'unavailable';
  trades: TradeOut[];
}

/** Equity/benchmark/markers/event log for a run. `complete` false means the
 *  event log was lost to a restart — the curve and trades still stand. */
export interface BacktestChartPayload {
  complete: boolean;
  note?: string;
  meta?: {
    symbol?: string;
    timeframe?: string;
    initial_cash?: number;
    final_equity?: number | null;
    [key: string]: unknown;
  };
  equity?: Array<{ time: EpochSeconds; value: number }>;
  /** See `BacktestRunDetail.equity_source`. [E6] */
  equity_source?: 'stored' | 'rebuilt' | 'unavailable';
  benchmark?: Array<{ time: EpochSeconds; value: number }>;
  drawdown?: Array<{ time: EpochSeconds; value: number }>;
  trades?: TradeOut[];
  /** Every signal evaluated, including the rejected ones and the reason. */
  events?: BacktestEvent[];
  metrics?: JsonObject;
  [key: string]: unknown;
}

/**
 * One decision the engine made.
 *
 * The rejected ones matter more than the accepted ones: "why didn't it
 * trade?" is the single most common question anyone asks their own system.
 */
export interface BacktestEvent {
  t?: EpochSeconds;
  time?: IsoDateTime;
  kind?: string;
  symbol?: string;
  side?: Side;
  price?: number;
  reason?: string;
  reason_code?: string;
  accepted?: boolean;
  context?: JsonObject;
  [key: string]: unknown;
}

/**
 * How a run's P&L can be sliced by calendar period.
 *
 * The order here is the order the segmented control renders them in —
 * coarsest first, because "how did each year do" is the question the owner
 * actually asked and the finer slices are drill-downs from it.
 */
export const PERIOD_GRANULARITIES = ['year', 'quarter', 'month', 'week', 'day'] as const;
export type PeriodGranularity = (typeof PERIOD_GRANULARITIES)[number];

/**
 * One calendar bucket of a run's trades, from
 * `GET /backtest/runs/{id}/periods`.
 *
 * A trade lands in the bucket its ENTRY falls in (`basis: entry` — echoed in
 * the summary so the screen states the attribution rule instead of leaving
 * the reader to guess which side of a month boundary a trade counted on).
 * Money is a decimal string, never a float round-trip [E1].
 */
export interface RunPeriodRow {
  /** Bucket label: `2024`, `2024-Q3`, `2024-07`, `2024-W31`, `2024-07-14`. */
  period: string;
  trades: number;
  wins: number;
  win_rate_pct: number | null;
  pnl: DecimalString;
  /** Running total up to and including this bucket, in entry order. */
  cum_pnl: DecimalString;
  fees: DecimalString;

  /**
   * The FULL growth-table column set, present only on `year` / `quarter` /
   * `month`; every one is `null` on `week` and `day`, which keep the lean five
   * columns. Year rows carry `setups` and `skipped_ceiling` when the run's
   * frozen year evidence reconciles to its trade ledger; quarter/month keep
   * them `null` because persisted trades cannot reconstruct refused setup
   * events. A fabricated 100% fill is what the year table refuses.
   * `settled_drawdown` is a fraction (0.31 = 31%).
   * `sharpe` is `null` on every quarter and month by the ≥180-day guard.
   */
  setups?: number | null;
  skipped_ceiling?: number | null;
  targets?: number | null;
  stops?: number | null;
  timeouts?: number | null;
  gross_profit?: DecimalString | null;
  gross_loss?: DecimalString | null;
  profit_factor?: number | null;
  settled_drawdown?: number | null;
  sharpe?: number | null;
  sharpe_stderr?: number | null;
  return_days?: number | null;
  peak_concurrent?: number | null;
  avg_hold_hours?: number | null;
}

/**
 * The extremes of a period table. Only the fields the summary strip prints —
 * a full `RunPeriodRow` also satisfies this shape, so the backend is free to
 * send either.
 */
export interface RunPeriodExtreme {
  period: string;
  pnl: DecimalString;
}

export interface RunPeriodsSummary {
  /**
   * The median bucket's P&L — the owner asked for the median by name: the
   * mean of eight years is one lucky year in disguise, the median is not.
   */
  median_pnl: DecimalString | null;
  /** Buckets that closed positive, out of `total`. */
  green: number;
  total: number;
  best: RunPeriodExtreme | null;
  worst: RunPeriodExtreme | null;
  /** Which timestamp assigned a trade to its bucket. */
  basis: 'entry' | 'exit';
  granularity: PeriodGranularity;
}

export interface RunPeriodsOut {
  rows: RunPeriodRow[];
  summary: RunPeriodsSummary;
  /**
   * The pinned TOTAL row for the full-column granularities (year / quarter /
   * month). `null` on week / day. Its `settled_drawdown` and `sharpe` are
   * WHOLE-run figures, not a sum of the buckets'.
   */
  total: RunPeriodRow | null;
}

export interface BacktestCompareResponse {
  runs: BacktestRunSummary[];
  /** False when the runs differ in a way that makes comparison misleading. */
  comparable: boolean;
  incomparable_reasons: string[];
}

export interface BacktestRunQuery {
  strategy_def_id?: number;
  symbol?: string;
  timeframe?: Timeframe;
  status?: BacktestStatus;
  /** Free text over the run's label and its strategy name. */
  q?: string;
  /**
   * Drop runs that took no trades. A `running` run is never dropped by this:
   * it has not taken a trade *yet*, which is a different fact from having
   * taken none, and hiding it makes a backtest vanish between pressing Run
   * and its first entry.
   */
  hide_empty?: boolean;
  /**
   * Include runs the owner archived *alongside* the ones he did not. They
   * are hidden by default, never deleted.
   */
  include_archived?: boolean;
  /**
   * The archive drawer: **only** runs the owner archived.
   *
   * A different question from `include_archived`, which returns everything
   * with the archived runs mixed back in and so can never answer "what did
   * I put away?". "Hidden because it took no trades" and "the owner
   * archived this" are different states and must not share a switch.
   */
  archived_only?: boolean;
  /**
   * Order the WHOLE filtered set by one column, server-side, before the
   * page is cut from it. Sorting the ten rows a page happens to hold is a
   * claim about a registry the client never saw.
   */
  sort?: RunSortKey;
  direction?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/** The columns the registry can be ordered by. Mirrors `Repositories.SORT_KEYS`. */
export type RunSortKey =
  | 'run_id'
  | 'label'
  | 'instrument'
  | 'trades'
  | 'return'
  | 'drawdown'
  | 'expectancy'
  | 'final_value'
  | 'fees'
  | 'status'
  | 'created';

/** Symbols and timeframes present across the WHOLE registry, not one page. */
export interface BacktestRunFacets {
  symbols: string[];
  timeframes: Timeframe[];
  /**
   * Every distinct fee rate in the registry. One entry and the fee column
   * is N identical strings; more than one and it is the only thing on the
   * screen saying two rows are not comparable. A whole-registry fact, so
   * the answer does not change with the page size.
   */
  fee_rates: string[];
  /** Every `name vN` the registry holds. Same purpose, same reasoning. */
  strategies: string[];
}

export interface ArchiveRunRequest {
  archived: boolean;
}

// ── Parameter sweep ───────────────────────────────────────────────────

export type OptimiseMetric =
  | 'expectancy_r'
  | 'sharpe'
  | 'sortino'
  | 'calmar'
  | 'profit_factor'
  | 'total_return_pct'
  | 'max_drawdown_pct'
  | 'win_rate_pct'
  | 'total_trades';

export interface OptimiseRequest {
  strategy: StrategySourceIn;
  symbol: string;
  timeframe: Timeframe;
  start?: IsoDateTime | null;
  end?: IsoDateTime | null;
  initial_cash?: DecimalString | number;
  /** Parameter name -> explicit list of values. Up to three axes. */
  parameters: Record<string, JsonValue[]>;
  metric?: OptimiseMetric;
  costs?: CostModelIn;
  sizer?: SizerIn;
  kill_switch?: KillSwitchIn;
  allow_shorts?: boolean;
  max_trials?: number;
  max_bars?: number;
}

export interface OptimiseCell {
  params: JsonObject;
  score: number | null;
  metrics: Record<string, number | null>;
  trades: number;
  error: string | null;
}

export interface OptimiseAxis {
  name: string;
  values: JsonValue[];
}

export interface OptimiseResponse {
  metric: string;
  higher_is_better: boolean;
  axes: OptimiseAxis[];
  cells: OptimiseCell[];
  /** Row-major over axes[0] x axes[1]. Only for a 2-D sweep. */
  matrix: Array<Array<number | null>> | null;
  /** 3x3 neighbourhood mean. Selecting on this is the cheapest
   *  overfitting defence there is. */
  smoothed_matrix: Array<Array<number | null>> | null;
  best: OptimiseCell | null;
  /** Argmax of the smoothed surface. When it differs from `best`, the raw
   *  peak is a spike and should not be traded. */
  plateau_best: OptimiseCell | null;
  plateau_warning: string | null;
  trials: number;
  failed: number;
  /** Share of trials with a positive score. Below ~0.5 the "best" cell is a
   *  survivor of noise, not an edge. */
  profitable_fraction: number | null;
}

// ══════════════════════════════════════════════════════════════════════
//  Strategies
// ══════════════════════════════════════════════════════════════════════

export interface StrategyCreateRequest {
  name: string;
  kind: StrategyKind;
  description?: string;
  params?: JsonObject;
  code_ref?: string | null;
}

export interface StrategySummaryOut {
  id: number;
  name: string;
  /** Versions are never overwritten. Saving `name` again creates N+1. */
  version: number;
  kind: StrategyKind;
  description: string;
  is_archived: boolean;
  created_at: IsoDateTime;
  updated_at: IsoDateTime;
}

export interface StrategyDetailOut extends StrategySummaryOut {
  code_ref: string | null;
  params: JsonObject;
  /** Human rendering of the rule tree, if any. */
  summary: string | null;
  required_timeframes: Timeframe[];
  required_indicators: string[];
}

/** `errors` is `field: message` lines, not a pydantic traceback — the
 *  person reading it is looking at a form and it is their money. */
export interface RuleSpecValidation {
  valid: boolean;
  errors: string[];
  summary: string | null;
  required_indicators: string[];
  required_timeframes: Timeframe[];
  warmup_bars: number | null;
}

export interface BuiltinStrategyOut {
  name: string;
  class_name: string;
  description: string;
  parameters: IndicatorParamSpec[];
}

export interface StrategyQuery {
  include_archived?: boolean;
  kind?: StrategyKind;
  q?: string;
  limit?: number;
  offset?: number;
}

// ══════════════════════════════════════════════════════════════════════
//  Lifecycle — the promotion ladder
// ══════════════════════════════════════════════════════════════════════

export interface LifecycleCapabilities {
  available: boolean;
  module: string;
  error: string | null;
  stages: LifecycleStage[];
  gate_sets: string[];
}

export interface LifecycleRegisterRequest {
  strategy_def_id: number;
  notes?: string;
}

/**
 * One promotion criterion.
 *
 * `matters_because` is carried through deliberately: a gate that fails
 * without saying why it exists is a gate somebody will override without
 * understanding what they are giving up. Always render it.
 */
export interface GateResultOut {
  name: string;
  code: string;
  status: string;
  passed: boolean;
  observed: number | null;
  threshold: number | null;
  comparison: string | null;
  detail: string;
  matters_because: string;
}

/** Promotion is a state transition, not a vibe. */
export interface ScorecardOut {
  strategy_def_id: number;
  stage: LifecycleStage;
  target_stage: LifecycleStage | null;
  passed: boolean;
  gate_set: string;
  gates: GateResultOut[];
  /** Codes of the gates that failed. */
  blocking: string[];
  evaluated_at: IsoDateTime;
}

export interface PromotionRequest {
  strategy_def_id: number;
  target_stage: LifecycleStage;
  /** 10–2000 characters. Kept forever. */
  reason: string;
  /** A run id rather than metrics, so a caller cannot hand the gates the
   *  numbers it would like them to see. */
  backtest_run_id?: number | null;
}

export interface LifecycleOverrideRequest {
  strategy_def_id: number;
  target_stage: LifecycleStage;
  backtest_run_id?: number | null;
  /** 40–4000 characters. Overriding a gate needs an argument, not a word. */
  reason: string;
  /** The exact gate codes being overridden. Naming them stops one override
   *  from silently covering a gate that failed later. */
  acknowledge_gates: string[];
}

export interface LifecycleVersionOut {
  strategy_def_id?: number;
  name?: string;
  version?: number;
  stage?: LifecycleStage;
  [key: string]: unknown;
}

export interface LifecycleEventOut {
  from_stage?: LifecycleStage | null;
  to_stage?: LifecycleStage;
  reason?: string;
  actor?: string;
  created_at?: IsoDateTime;
  [key: string]: unknown;
}

// ══════════════════════════════════════════════════════════════════════
//  Portfolio
// ══════════════════════════════════════════════════════════════════════

/**
 * An open position.
 *
 * `is_fully_protected` is the field to watch. False means resting stops do
 * not cover the whole size — the highest-severity condition this system can
 * report, and the exact situation this platform exists to prevent.
 */
export interface PositionOut {
  position_id: string;
  deployment_id: number | null;
  symbol: string;
  side: Side;
  quantity: DecimalString;
  entry_price: DecimalString;
  /** There is no such thing as a position without one. */
  stop_price: DecimalString;
  take_profit_price: DecimalString | null;
  protected_quantity: DecimalString;
  /** Order ids of the stops actually resting on the exchange. */
  protective_order_ids: string[];
  is_fully_protected: boolean;
  total_risk: DecimalString;
  distance_to_stop_pct: number | null;
  /** From locally stored candles, so it may be stale. */
  mark_price: DecimalString | null;
  unrealised_pnl: DecimalString | null;
  r_multiple: number | null;
  strategy_id: string | null;
  opened_at: IsoDateTime;
}

export interface BalanceOut {
  asset: string;
  free: DecimalString;
  locked: DecimalString;
  total: DecimalString;
}

/** Mark-to-market including open positions, so the drawdown shown is the
 *  one that was actually lived through. */
export interface EquityPointOut {
  t: EpochSeconds;
  time: IsoDateTime;
  equity: number;
  cash: number;
  realised_pnl: number;
  unrealised_pnl: number;
  peak_equity: number;
  drawdown_pct: number;
  open_positions: number;
}

export interface DailyPnLOut {
  /** `YYYY-MM-DD`, UTC. Crypto has no session; the boundary is by definition. */
  day: string;
  opening_equity: DecimalString;
  closing_equity: DecimalString;
  pnl: DecimalString;
  pnl_pct: DecimalString;
  max_drawdown_pct: DecimalString;
}

/**
 * A completed round trip.
 *
 * `r_multiple` is the number to judge it by: R survives a change in account
 * size and in position sizing, which makes it the only per-trade figure
 * worth comparing across time.
 */
export interface TradeOut {
  trade_id: string;
  deployment_id: number | null;
  backtest_run_id: number | null;
  symbol: string;
  side: Side;
  quantity: DecimalString;
  entry_price: DecimalString;
  exit_price: DecimalString;
  stop_price: DecimalString;
  entry_time: IsoDateTime;
  exit_time: IsoDateTime;
  entry_t: EpochSeconds;
  exit_t: EpochSeconds;
  fees: DecimalString;
  gross_pnl: DecimalString;
  net_pnl: DecimalString;
  initial_risk: DecimalString;
  r_multiple: number;
  is_winner: boolean;
  exit_reason: string;
  strategy_id: string | null;
  duration_hours: number;

  /**
   * What risk-based sizing asked for before any cap, and what clipped it. [A2]
   *
   * `quantity` alone cannot tell "a wider stop, so a smaller position" —
   * correct, and the whole point of risk-based sizing — from "the ask was
   * larger than the account and got clipped", which means the risk actually
   * taken was below the risk configured for the run. Null on trades recorded
   * before this was tracked: unknown, not "not capped".
   */
  requested_quantity: DecimalString | null;
  size_cap: SizeCap;
  /** Percent of the requested size that was not taken. Null when unrecorded. */
  size_shortfall_pct: number | null;

  /**
   * What leverage did to this trade. [F1]
   *
   * The owner asked for these by name: "you need to show it in the trades tab
   * also how you are taking the leverage." `margin_posted` is the cash the
   * position tied up (`notional / leverage`) and answers "why can I only hold
   * three of these at once". `risk_pct_taken` is the risk actually carried —
   * equal to the run's configured risk when nothing capped the size, and below
   * it when something did, as low as 1.13% against a configured 5% on run 30.
   */
  leverage: DecimalString;
  sizing_equity: DecimalString | null;
  margin_posted: DecimalString | null;
  risk_pct_taken: number | null;

  /**
   * Where the stop was at the end, and whether it had moved. [F6]
   *
   * `stop_price` above is the stop the **size** was derived from: `r_multiple`
   * and `initial_risk` divide by that distance, so it must keep meaning that.
   * It is not where a trailed trade exited. With only that column on the row,
   * a breakeven scratch printed `exit_stop` beside a stop 5-8% away from its
   * own close, which is a report describing something that did not happen.
   *
   * Measured on the owner's configuration: 53 stops moved, 30 of those trades
   * then exited on the moved stop at a mean −0.157R, against −1.148R for the
   * trades whose stop never moved. `exit_reason` said `exit_stop` for both.
   *
   * `final_stop_price` is null on rows written before this was recorded —
   * unknown, not "the stop never moved".
   */
  final_stop_price: DecimalString | null;
  /** The stop in force at exit — `final_stop_price ?? stop_price`. */
  effective_stop_price: DecimalString | null;
  stop_was_moved: boolean;
  stop_moved_to_breakeven: boolean;
  /** Closed ON a moved stop. 53 stops moved on the owner's run; 30 exited there. */
  exited_at_breakeven: boolean;
  /** `exit_reason`, refined: `exit_stop_breakeven` for a scratched stop-out. */
  exit_reason_detail: string;

  /**
   * The entry signal's own metadata, verbatim. [G7]
   *
   * Everything above is the platform's account of the trade. This is the
   * **strategy's** — the keys are its vocabulary, not the API's, so this is an
   * open record and not a fixed shape. `liquidity_sweep_choch` sets nine:
   * `trend_gate_timeframes`, `sweep_window_bars`, `choch_window_bars`,
   * `eq_lookback_bars`, `stop_mode`, `sweep_extreme`, `choch_level`,
   * `stop_distance_pct`, `liquidation_distance_pct`.
   *
   * It reached no screen until now — the engine dropped it between the signal
   * and the position, so it lived only in backend unit tests. Measured on run
   * #98 (BTCUSDT 4h, 100 trades): every row came back without it.
   *
   * The chart payload sends the same key with the same name, so
   * `normaliseTrades` reads one spelling for both wire shapes. Empty `{}` on
   * a strategy that annotates nothing and on any trade recorded before this
   * travelled — the two are not distinguished, because nothing is derived
   * from it.
   */
  signal_metadata: Record<string, number | string | boolean>;
}

/** Compound sizes off current equity; fixed sizes off starting capital. [F2] */
export type CapitalMode = 'compound' | 'fixed';

/**
 * How much leverage a symbol's stops tolerate, from
 * `GET /api/backtest/leverage-limits`. [F1/A6]
 *
 * `max_safe_leverage` is the highest multiple at which the STOP still fires
 * before the exchange liquidates, derived from the **widest** stop recorded
 * for the symbol — the strategy refuses per setup, so a run completes only at
 * a leverage its widest stop tolerates. Null when there is no stored history
 * to measure: unknown, not "all safe".
 */
export interface LeverageLimitsOut {
  symbol: string;
  ceiling: number;
  stop_pct: number | null;
  stop_pct_source: 'requested' | 'widest_recorded' | 'unknown';
  median_stop_pct: number | null;
  measured_from_trades: number;
  maintenance_margin_rate: number;
  safety_buffer: number;
  max_safe_leverage: number | null;
}

/**
 * Which constraint decided a position's size.
 *
 * `risk_budget` means nothing clipped it. The others each mean the position
 * is smaller than the risk setting implies.
 */
export type SizeCap = 'risk_budget' | 'notional_cap' | 'cash' | 'quantity_step';

/** Aggregate trade statistics. Undefined values are null, never zero. */
export interface PerformanceStatsOut {
  trade_count: number;
  trades_covered: number;
  trades_total: number;
  truncated: boolean;
  wins: number;
  losses: number;
  win_rate_pct: number | null;
  gross_pnl: DecimalString;
  net_pnl: DecimalString;
  fees: DecimalString;
  avg_win: DecimalString;
  avg_loss: DecimalString;
  largest_win: DecimalString;
  largest_loss: DecimalString;
  total_r: number;
  total_r_excluding_best: number;
  worst_losing_streak: number;
  expectancy_r: number;
  profit_factor: number | null;
  /** If `stop_loss` dominates the loss column, the strategy is telling you
   *  something no Sharpe ratio will. */
  exit_reasons: Record<string, number>;
  /** Fees as a percent of gross profit. Decides whether a fast timeframe
   *  is viable at all. */
  fee_drag_pct: number | null;
  r_distribution: Array<{
    label: string;
    from: number;
    to: number;
    count: number;
    wins: number;
  }>;
}

export interface TradeQuery {
  deployment_id?: number;
  backtest_run_id?: number;
  symbol?: string;
  start?: IsoDateTime;
  end?: IsoDateTime;
  side?: Side;
  outcome?: 'all' | 'wins' | 'losses';
  exit_reason?: string;
  min_r?: number;
  max_r?: number;
  sort?:
    | 'exit_time'
    | 'entry_time'
    | 'net_pnl'
    | 'r_multiple'
    | 'symbol'
    | 'fees'
    | 'quantity'
    | 'entry_price'
    | 'exit_price'
    | 'stop_price'
    | 'leverage';
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export type AttributionDimension =
  | 'symbol'
  | 'strategy'
  | 'exit_reason'
  | 'side'
  | 'hour_of_day'
  | 'day_of_week';

/**
 * One-screen portfolio state. Deliberately answers "is anything exposed"
 * before "how much did I make".
 */
export interface PortfolioSummary {
  open_positions: number;
  unprotected_positions: number;
  unprotected_position_ids: string[];
  open_risk: DecimalString;
  equity: DecimalString;
  peak_equity: DecimalString;
  drawdown_pct: number;
  deployments: {
    total: number;
    running: number;
    [key: string]: unknown;
  };
  /**
   * Today's realised result: closed round trips only, net of fees, bucketed
   * by UTC day. `net_pnl` is money on the account (E1, money-not-R); the only
   * float here is the backend-computed `expectancy_r`, shown as a labelled
   * sub-figure and never as a headline. Emitted by `routes_portfolio.py`;
   * promoted here from the open index signature so the Dashboard reads it
   * typed rather than narrowing `unknown` at the call site.
   */
  today: {
    trades: number;
    net_pnl: DecimalString;
    expectancy_r: number;
  };
  /** Alerts nobody has cleared — a count of outstanding work, not a badge. */
  unacknowledged_alerts: number;
  /** When the book was marked. UTC; marks come from the local candle store. */
  as_of: IsoDateTime;
  [key: string]: unknown;
}

// ══════════════════════════════════════════════════════════════════════
//  Portfolio analytics — the three blobs, now typed
// ══════════════════════════════════════════════════════════════════════
//
// These mirror the pydantic models in `app.analytics` (portfolio.py,
// attribution.py, performance.py, excursion.py) that `routes_portfolio.py`
// returns via `model_dump(mode="json")`. Correlation, attribution,
// performance and excursion previously arrived as `JsonObject` and were
// parsed defensively at every call site; the wire contract is fixed here so
// the frontend selects and formats fields (E2) rather than re-discovering
// their shape. Every aggregate below is computed in Python — nothing on this
// wire is derived in React.

/**
 * A metric that may legitimately not exist.
 *
 * Mirrors `analytics.performance.Stat`. Exactly one of `value` /
 * `undefined_reason` is set: a null value carries a plain-English reason the
 * owner is shown verbatim, so "profit factor undefined (no losing trades)"
 * never renders as `0.00` or as a blank cell.
 */
export interface Stat {
  value: number | null;
  undefined_reason: string | null;
}

export type CorrelationSeverity = 'info' | 'warning' | 'critical';

/**
 * One pair of deployments (or, once grouped, strategies). Four correlations
 * because each lies on its own: Pearson is fooled by a single shared crash,
 * Spearman is not; `tail_correlation` is measured over the worst decile of
 * portfolio days — the number that predicts the drawdown; `co_active_correlation`
 * removes the days both were flat, which otherwise drags correlation to zero.
 */
export interface CorrelationPair {
  a: string;
  b: string;
  pearson: number | null;
  spearman: number | null;
  tail_correlation: number | null;
  co_active_correlation: number | null;
  overlap_days: number;
  co_active_days: number;
  severity: CorrelationSeverity;
  /** The analytics module's own sentence about the pair. Rendered verbatim. */
  message: string;
}

export interface CorrelationReport {
  strategy_ids: string[];
  method: 'pearson' | 'spearman';
  matrix: Array<Array<number | null>>;
  pairs: CorrelationPair[];
  /** Null on the "fewer than two streams" branch, a `Stat` otherwise. */
  average_pairwise: Stat | null;
  warnings: string[];
  /**
   * REQUIRED-BACKEND-API 5: a returned dangerous-pair count and one-bet
   * grade so the Overview headline is a backend scalar, not a client-side
   * count of `pairs[]` (E2). Absent today — the Overview renders a defined
   * pending state for the count rather than counting pairs here.
   */
  dangerous_pair_count?: number | null;
  one_bet_grade?: string | null;
}

/** One bucket of `AttributionReport.rows`. Money-first; R fields are diagnostics. */
export interface AttributionRow {
  bucket: string;
  trades: number;
  wins: number;
  losses: number;
  gross_pnl: number;
  fees: number;
  net_pnl: number;
  /** Contribution to return: this bucket's share of net P&L. */
  pct_of_net_pnl: Stat;
  /**
   * Share of book variance (cov-with-book / var-book), sums to ~100 across
   * buckets. A bucket can carry more of the flush risk than of the profit —
   * this is the column the Attribution panel exists to surface.
   */
  risk_contribution_pct: Stat;
  win_rate_pct: Stat;
  profit_factor: Stat;
  expectancy: Stat;
  expectancy_r: Stat;
  total_r: number;
  average_win: Stat;
  average_loss: Stat;
  largest_win: number;
  largest_loss: number;
  net_pnl_ex_top1: number;
  net_pnl_ex_top2: number;
  top_trade_share_pct: Stat;
  trades_for_half_of_profit: number;
  luck_flagged: boolean;
  luck_message: string | null;
  first_trade: IsoDateTime | null;
  last_trade: IsoDateTime | null;
}

export interface AttributionReport {
  dimension: string;
  rows: AttributionRow[];
  total_net_pnl: number;
  total_trades: number;
  best_contributor: string | null;
  worst_contributor: string | null;
  warnings: string[];
}

/** One completed or still-open drawdown episode. `depth_pct` is negative. */
export interface DrawdownEpisode {
  peak_time: IsoDateTime;
  valley_time: IsoDateTime;
  recovery_time: IsoDateTime | null;
  peak_equity: number;
  valley_equity: number;
  /** Negative. -18.4 means down 18.4%. */
  depth_pct: number;
  days_to_valley: number;
  days_to_recovery: number | null;
  /** Days from peak to recovery (or to the last mark, if still open). */
  days_underwater: number;
  /** Right-censored: not yet recovered, so `days_underwater` is a lower bound. */
  is_ongoing: boolean;
}

/** Return over one calendar month (`2026-03`) or year (`2026`). */
export interface PeriodReturn {
  period: string;
  return_pct: number;
  end_equity: number;
}

/**
 * Risk and risk-adjusted statistics for one equity curve, annualised on 365
 * days. Mirrors `analytics.performance.PerformanceReport`.
 */
export interface PerformanceReport {
  label: string;
  start: IsoDateTime | null;
  end: IsoDateTime | null;
  observations: number;
  elapsed_days: number;
  periods_per_year: number;
  initial_equity: number;
  final_equity: number;
  total_return_pct: number;
  cagr_pct: Stat;
  volatility_pct: Stat;
  downside_deviation_pct: Stat;
  skew: Stat;
  excess_kurtosis: Stat;
  max_drawdown_pct: number;
  max_drawdown: DrawdownEpisode | null;
  longest_drawdown: DrawdownEpisode | null;
  ongoing_drawdown: DrawdownEpisode | null;
  time_underwater_pct: number;
  ulcer_index: Stat;
  pain_index: Stat;
  var_95_pct: Stat;
  modified_var_95_pct: Stat;
  cvar_95_pct: Stat;
  sharpe: Stat;
  sharpe_standard_error: Stat;
  probabilistic_sharpe: Stat;
  sortino: Stat;
  calmar: Stat;
  omega: Stat;
  tail_ratio: Stat;
  monthly_returns: PeriodReturn[];
  annual_returns: PeriodReturn[];
  /** Running drawdown per daily observation, non-positive, a new high prints 0. */
  underwater_pct: number[];
  warnings: string[];
}

/** One trade's maximum adverse / favourable excursion. */
export interface ExcursionRecord {
  trade_id: string;
  symbol: string;
  side: Side;
  strategy_id: string | null;
  exit_reason: string;
  entry_price: number;
  exit_price: number;
  stop_price: number;
  risk_per_unit: number;
  net_pnl: number;
  r_multiple: number;
  is_winner: boolean;
  bars_observed: number;
  bars_held: number;
  mae_pct: number;
  mfe_pct: number;
  mae_r: number;
  mfe_r: number;
  mae_atr: number | null;
  mfe_atr: number | null;
  bars_to_mae: number;
  bars_to_mfe: number;
  capture_ratio: number | null;
  stop_censored: boolean;
  ambiguous: boolean;
  mae_path_r: number[];
  mfe_path_r: number[];
}

/**
 * One point on the counterfactual stop sweep. R-space by construction — these
 * are diagnostics, badged as R, never headline money (see §0 of the spec and
 * REQUIRED-BACKEND-API 4 for the money conversion the backend owes).
 */
export interface StopSweepPoint {
  stop_multiple: number;
  expectancy_r: number;
  total_r: number;
  win_rate_pct: number;
  profit_factor: Stat;
  max_drawdown_r: number;
  trades_stopped: number;
  winners_killed: number;
  reliable: boolean;
  caveat: string | null;
}

export interface EdgeRatioPoint {
  bars_after_entry: number;
  mean_mfe_r: number;
  mean_mae_r: number;
  edge_ratio: number;
  sample: number;
}

/**
 * The excursion payload: `ExcursionSet` plus the optional stop sweep and edge
 * ratio the endpoint appends when `include_stop_sweep` is set. `ambiguous_pct`
 * and `stop_censored_pct` are the reliability gate — render them or withhold
 * the chart.
 */
export interface ExcursionReport {
  records: ExcursionRecord[];
  skipped_trade_ids: string[];
  ambiguous_pct: number;
  stop_censored_pct: number;
  reliable: boolean;
  warnings: string[];
  stop_sweep?: StopSweepPoint[];
  edge_ratio?: EdgeRatioPoint[];
}

// ══════════════════════════════════════════════════════════════════════
//  Deployments
// ══════════════════════════════════════════════════════════════════════

export interface DeploymentCreateRequest {
  strategy_def_id: number;
  name: string;
  symbol: string;
  timeframe: Timeframe;
  environment: Environment;
  venue?: Venue;
  allocated_capital: DecimalString | number;
  risk_per_trade_pct?: DecimalString | number;
  max_daily_loss_pct?: DecimalString | number | null;
  max_drawdown_pct?: DecimalString | number;
  max_open_positions?: number;
  params?: JsonObject;
  // ── The deploy contract (Phase 3), frozen server-side at create ──────
  /** The authoritative FUTURES run this deploy rests on. Validated + its
   *  evidence frozen into `spec_snapshot` (server-side). */
  backtest_run_id?: number | null;
  /** The variant's family/category (e.g. "S2 Base") — the Family grouping key. */
  family_code?: string | null;
  /** additive | compounding — part of the variant identity. */
  compounding_mode?: string | null;
  /** The variant's leverage (part of its identity — monitoring spec §3.2). */
  leverage?: number | null;
  /** The mandatory operator comment on a passing deploy. */
  deploy_comment?: string | null;
  /** The preflight scorecard the user acted on. */
  gate_scorecard_snapshot?: JsonObject;
}

/** Every transition carries a reason into the audit trail. */
export interface DeploymentActionRequest {
  reason: string;
}

/**
 * One deploy decision, one deployment per market (a basket variant). `symbols`
 * names the markets; omitted → every market the evidence run traded. The
 * server names each row `<name> · <symbol> · <environment>` and stamps a shared
 * `basket_ref` into params so Monitoring groups them.
 */
export interface DeploymentBasketCreateRequest extends Omit<DeploymentCreateRequest, 'symbol'> {
  symbol?: string | null;
  symbols?: string[] | null;
}

export interface DeploymentBasketOut {
  basket_ref: string;
  deployments: DeploymentOut[];
}

export type ServerGateSeverity = 'pass' | 'fail' | 'caution' | 'pending';

/** One gate of the SERVER deploy contract — the shape `PreflightScorecard` draws. */
export interface ServerGateOut {
  code: string;
  name: string;
  severity: ServerGateSeverity;
  observed: number | string | null;
  threshold: number | null;
  comparison: string | null;
  detail: string;
  matters_because: string;
  blocking: boolean;
}

/** The scorecard `POST /deployments` freezes onto the record (source `server_deploy_contract`). */
export interface ServerScorecardOut {
  source: string;
  passed: boolean;
  gate_set: string;
  environment: Environment;
  strategy_stage: string;
  evidence_run_id: number | null;
  evidence_fingerprint: string | null;
  spec_fingerprint: string;
  live_strategy_ref: string | null;
  exit_model: string | null;
  gates: ServerGateOut[];
  blocking_codes: string[];
  blocked_reasons: string[];
  hard_reject_drawdown: boolean;
  evaluated_at: string;
  comment: string | null;
}

/** `POST /deployments/preflight` — the server's verdict per market, nothing created. */
export interface DeployContractPreflightOut {
  environment: Environment;
  symbols: string[];
  passed: boolean;
  spec_fingerprint: string;
  scorecards: Record<string, ServerScorecardOut>;
}

export interface DeploymentOut extends DeploymentRecordFields {
  id: number;
  /** The stable public reference (`DEP-{year}-{seq}`) the deployment is tracked
   *  by from live → closed → history. Assigned once, never changes. */
  public_ref: string;
  strategy_def_id: number;
  strategy_name: string | null;
  strategy_version: number | null;
  name: string;
  venue: Venue;
  /** The exchange account this deployment trades on. Null until a live account
   *  is attached (the Monitoring Control Center fills it). */
  exchange_account_ref?: string | null;
  /** Shared by the deployments one basket deploy created; null for a single deploy. */
  basket_ref?: string | null;
  /** Optimistic-concurrency counter for the Control Center's command state
   *  machine. Inert here; surfaced for completeness. */
  row_version?: number;
  environment: Environment;
  symbol: string;
  timeframe: Timeframe;
  status: DeploymentStatus;
  /** Operator intent; differs from `status` until the separate executor
   * acknowledges the requested transition. */
  desired_status: DeploymentStatus;
  execution_state: ExecutionState;
  executor_id: string | null;
  executor_heartbeat_at_ms: number | null;
  runner_observation?: RunnerObservation | null;
  last_command_id: string | null;
  last_executor_error: string | null;
  legacy_contract_warning: string | null;
  contract_confirmed_at_ms: number | null;
  halt_reason: string | null;
  risk_per_trade_pct: DecimalString;
  max_daily_loss_pct: DecimalString | null;
  max_drawdown_pct: DecimalString | null;
  risk_contract_error?: string | null;
  max_open_positions: number;
  allocated_capital: DecimalString;
  open_positions: number;
  /** Positions whose resting stops do not cover the whole size. Anything
   *  above zero is an emergency. */
  unprotected_positions: number;
  params: JsonObject;
  created_at: IsoDateTime;
  updated_at: IsoDateTime;
  started_at: IsoDateTime | null;
  stopped_at: IsoDateTime | null;
  /** A RUNNING row with no runner is the "running bot is not a trading bot"
   *  failure, made visible. */
  runner_attached: boolean;
}

export interface DeploymentStatusOut {
  deployment_id: number;
  status: DeploymentStatus;
  desired_status?: DeploymentStatus;
  execution_state?: ExecutionState;
  halt_reason: string | null;
  runner_attached?: boolean;
  open_positions?: number;
  unprotected_positions?: number;
  open_risk?: DecimalString;
  equity?: DecimalString | null;
  seconds_since_last_trade?: number | null;
  seconds_since_last_equity_snapshot?: number | null;
  [key: string]: unknown;
}

export type TradingCommandType =
  | 'start_deployment'
  | 'stop_deployment'
  | 'pause'
  | 'resume'
  | 'halt_deployment'
  | 'close_virtual_trade'
  | 'square_off_deployment';

export interface TradingCommandRequest {
  command_type: TradingCommandType;
  reason: string;
  deployment_id?: number | null;
  virtual_trade_id?: string | null;
  exchange_account_id?: number | null;
  symbol?: string | null;
  position_side?: 'both' | 'long' | 'short' | null;
  requested_quantity?: DecimalString | null;
  expected_trade_version?: number | null;
}

export interface TradingCommandAcceptedOut {
  command_id: string;
  command_type: TradingCommandType;
  status: string;
  deployment_id: number | null;
  requested_at_ms: number;
  idempotency_key: string;
}

export interface ExecutorStatusOut {
  connected: boolean;
  executor_id: string | null;
  environment: Environment | null;
  state: string;
  heartbeat_at_ms: number | null;
  heartbeat_age_ms: number | null;
  public_market_source: string;
  pending_commands: number;
}

export interface EngineRequestTelemetry {
  request_id: string;
  sequence: number;
  engine_id: string;
  owner: string;
  source: string;
  destination: string;
  purpose: string;
  method: string;
  endpoint: string;
  scope: string | null;
  request_weight: number;
  order_cost: number;
  requested_at_ms: number;
  sent_at_ms: number | null;
  completed_at_ms?: number | null;
  admission_wait_ms?: number | null;
  wire_duration_ms?: number | null;
  total_duration_ms?: number | null;
  elapsed_ms?: number | null;
  http_status?: number | null;
  state: 'rate_wait' | 'in_flight' | 'succeeded' | 'failed' | 'rate_limited';
  error_type?: string | null;
  blocked_until_ms?: number | null;
}

export interface EngineRequestCount {
  engine_id: string;
  method: string;
  endpoint: string;
  attempts: number;
  succeeded: number;
  failed: number;
  rate_limited: number;
}

export interface ExchangeEgressGateTelemetry {
  environment?: string;
  venue?: string;
  state: 'idle' | 'open' | 'blocked' | 'banned' | 'unavailable';
  weight_used_1m?: number;
  weight_limit_1m?: number;
  normal_ceiling_1m?: number;
  orders_used_10s?: number;
  orders_used_1m?: number;
  ws_connections_used_5m?: number;
  ws_connections_limit_5m?: number;
  ws_connections_normal_ceiling_5m?: number;
  blocked_seconds?: number;
  banned?: boolean;
  last_status?: number | null;
  last_component?: string | null;
  last_endpoint?: string | null;
  updated_at_ms?: number;
  error?: string;
}

/**
 * One account's safety inputs, as `app.projections.exchange_safety_inputs`
 * composes them. Per-account since migration 0047 (V2-I02/I07): a sibling
 * account's dead executor or frozen reconcile must never be reported against a
 * healthy one, so the engine room scopes the fault to the cell that has it.
 */
export interface EngineCitySafetyAccountOut {
  exchange_account_id: number;
  executor_available: boolean;
  lease_age_ms: number | null;
  epoch: number | null;
  /** The EXCHANGE `as_of_ms` the observation carries — never a per-sweep clock. */
  reconciliation_as_of_ms: number | null;
  reconciliation_stale: boolean;
}

export interface EngineCitySafetyOut {
  level: 'safe' | 'warning' | 'blocked';
  reason: string;
  headline: string;
  detail: string;
  until_ms: number | null;
  executor_available: boolean;
  executor_lease_age_ms: number | null;
  egress_state: string | null;
  reconciliation: 'none' | 'stale' | 'fresh';
  /** K1: `ok`, `no_receipt` (LIVE, fails closed) or `not_applicable_testnet`. */
  credential_state: string;
  credential_detail: string;
  accounts: EngineCitySafetyAccountOut[];
}

/**
 * SK1 — the independent gross-exposure kernel's block for one account.
 *
 * `enforced: false` with `state: "kernel_not_running"` is the state the platform
 * is actually in: `GrossCapKernel` exists but nothing constructs it, because the
 * owner deferred the kernel process. A deferred safety control must display as a
 * warning and never as green or as a neutral "none" (V2-I13).
 */
export interface EngineCityGrossCapOut {
  enforced: boolean;
  /** `kernel_not_running`, or the kernel level when a heartbeat is current. */
  state: string;
  level: string | null;
  cap: DecimalString | null;
  observed_gross: DecimalString | null;
  heartbeat_at_ms: number | null;
  heartbeat_age_ms: number | null;
  reason: string;
}

export interface EngineCityAccountCellOut {
  exchange_account_id: number;
  environment: string;
  venue: string;
  label: string | null;
  status: string;
  equity: DecimalString | null;
  maintenance_margin: DecimalString | null;
  /** maintenance / equity, computed at read. Null when equity is absent or zero. */
  margin_ratio: DecimalString | null;
  equity_as_of_ms: number | null;
  lease: {
    owner: string | null;
    epoch: number | null;
    heartbeat_age_ms: number | null;
    state: string | null;
  };
  order_windows: {
    ten_second_used: number | null;
    ten_second_limit: number | null;
    minute_used: number | null;
    minute_limit: number | null;
  };
  egress: {
    state: string | null;
    weight_used_1m: number | null;
    weight_limit_1m: number | null;
  };
  deployments: { count: number; ids: number[] };
  gross_cap: EngineCityGrossCapOut;
}

export interface EngineCityAccountsOut {
  accounts: EngineCityAccountCellOut[];
  count: number;
}

export interface EngineCityIncidentLineageStepOut {
  /** `raised` | `acknowledged` | `recovering` | `resolved`. */
  stage: string;
  at_ms: number;
  detail: string | null;
}

export interface EngineCityIncidentOut {
  incident_id: number;
  raised_at_ms: number;
  category: string;
  component: string;
  severity: string;
  status: string;
  scope: {
    exchange_account_id: number | null;
    symbol: string | null;
    position_side: string | null;
  };
  owner: string;
  recovery_owner: string;
  attempts: number;
  acknowledged_at_ms: number | null;
  resolved_at_ms: number | null;
  /** The reconciliation run that proved the invariant again. */
  recovery_run_id: string | null;
  /** The retained `reconciliation_snapshot` row that run wrote — the proof. */
  recovery_proof_event_id: number | null;
  /** OPEN and ACKNOWLEDGED are both open: an ack never resolves. */
  open: boolean;
  open_duration_ms: number | null;
  lineage: EngineCityIncidentLineageStepOut[];
}

export interface EngineCityIncidentsOut {
  incidents: EngineCityIncidentOut[];
  limit: number;
  count: number;
}

export interface EngineCitySummaryOut {
  deployments_by_status: Record<string, number>;
  execution_states: Record<string, number>;
  open_lots: number;
  unprotected_lots: number;
  gross_cap_levels: Record<string, number>;
  accounts: number;
}

export interface EngineCityEvidenceEventOut {
  id: number;
  kind: string;
  scope: string;
  level: string;
  as_of_ms: number;
}

export interface EngineCityEvidenceOut {
  events: EngineCityEvidenceEventOut[];
  cursor_ms: number;
}

export interface EngineCityTelemetryOut {
  captured_at_ms: number;
  egress_gate: ExchangeEgressGateTelemetry;
  api: {
    available: boolean;
    captured_at_ms: number | null;
    sequence: number;
    request_counts: EngineRequestCount[];
    market_feed: JsonObject;
    ticker: JsonObject;
    error: string | null;
  };
  executor: {
    available: boolean;
    executor_id: string | null;
    state: string;
    heartbeat_at_ms: number | null;
    heartbeat_age_ms: number | null;
    last_error: string | null;
    captured_at_ms: number | null;
    sequence: number;
    request_counts: EngineRequestCount[];
    rate_budget: JsonObject | null;
    market_feed: JsonObject | null;
    account_truth: JsonObject | null;
    runners: JsonObject | null;
  };
  /** Database plane health — the store every engine reads/writes. `connected`
   *  false (or a slow `responsive_ms` / growing `wal_mb`) means a degraded DB,
   *  surfaced in the Engine Room instead of only showing as downstream staleness.
   *  Optional so pre-existing telemetry fixtures/mocks stay valid; the backend
   *  always sends it and the render guards with `?.`. */
  database?: {
    connected: boolean;
    backend?: string;
    path?: string | null;
    responsive_ms?: number;
    size_mb?: number | null;
    wal_mb?: number;
    journal_mode?: string | null;
    foreign_keys?: boolean | null;
    error?: string;
  };
  requests: EngineRequestTelemetry[];
  retention: JsonObject;
  contracts: {
    account_snapshot_interval_ms: number;
    history_refresh_interval_ms: number;
    executor_heartbeat_interval_ms: number;
    entry_staleness_deadline_ms: number;
    public_ticker_interval_ms: number;
    parquet_edge_interval_ms: number;
  };
}

export interface PortfolioBookingOut {
  booking_id: string;
  deployment_id: number;
  deployment_ref: string;
  deployment_name: string;
  booking_type: 'square_off' | 'profit_sweep' | 'flat_reset';
  currency: string;
  amount: DecimalString;
  realized_pnl: DecimalString;
  fees: DecimalString;
  funding: DecimalString;
  reason: string;
  booked_at_ms: number;
}

export interface LogLineOut {
  t: EpochSeconds;
  time: IsoDateTime;
  level: string;
  source: string;
  message: string;
  context: JsonObject;
}

/** Live behaviour against the backtest that justified the deployment. */
export interface DriftReportOut {
  deployment_id: number;
  available: boolean;
  reason: string | null;
  payload: JsonObject;
}

// ══════════════════════════════════════════════════════════════════════
//  Alerts
// ══════════════════════════════════════════════════════════════════════

export interface AlertDelivery {
  channel: string;
  status: string;
  attempts: number;
  /** Provider failure text returned by /api/alerts. */
  last_error: string | null;
  sent_at: IsoDateTime | null;
}

export interface AlertOut {
  id: number;
  deployment_id: number | null;
  severity: AlertSeverity;
  category: string;
  title: string;
  message: string;
  context: JsonObject;
  dedupe_key: string | null;
  created_at: IsoDateTime;
  acknowledged_at: IsoDateTime | null;
  acknowledged_by: string | null;
  /** Per-channel delivery state, so "I never got told" and "it was
   *  rate-limited" stay distinguishable after the fact. */
  deliveries: AlertDelivery[];
}

export interface AlertAckRequest {
  alert_ids: number[];
  by?: string;
}

/** GET /api/alerts/unacknowledged/count. `critical` is the critical part of
 *  `count`, read by the same statement, so `critical <= count` always. */
export interface UnacknowledgedCountOut {
  count: number;
  critical: number;
  min_severity: AlertSeverity;
}

/** Channel configuration, with secrets reported as booleans only. */
export interface AlertConfigOut {
  channels: JsonObject[];
  routes: Record<string, string[]>;
  broadcast_types: string[];
  rate_limits: JsonObject;
  stats: Record<string, number>;
  service_available: boolean;
  service_error: string | null;
}

export interface AlertQuery {
  severity?: AlertSeverity;
  min_severity?: AlertSeverity;
  category?: string;
  deployment_id?: number;
  acknowledged?: boolean;
  since?: IsoDateTime;
  until?: IsoDateTime;
  limit?: number;
  offset?: number;
}

// ══════════════════════════════════════════════════════════════════════
//  Drawings — persisted chart overlays
// ══════════════════════════════════════════════════════════════════════

/**
 * One chart overlay, exactly as KLineChart emits it.
 *
 * The backend deliberately does not interpret `payload` — with one
 * exception it enforces: every point must carry a numeric `timestamp` and
 * `value`. A drawing anchored to a bar *index* is correct on exactly one
 * timeframe and wrong on the other nine, and it looks perfect on the one it
 * was drawn on, so its absence cannot be spotted later.
 */
export interface DrawingIn {
  overlay_id: string;
  overlay_type: string;
  payload: JsonObject;
  label?: string;
  /** Omit to leave the existing lock alone — an autosave from a stale tab
   *  must not unlock a level that was deliberately pinned. */
  locked?: boolean | null;
}

/**
 * A saved overlay. Belongs to the **symbol**, not to a timeframe.
 *
 * Anchored in (UTC timestamp, price), so it is defined at every interval: a
 * support level found on the 4h is the same line at 5m, drawn against a
 * finer ruler.
 */
export interface DrawingOut {
  id: number;
  symbol: string;
  /** Where it was drawn. Provenance for a "drawn on 4h" chip — never a filter. */
  drawn_on_timeframe: Timeframe;
  /** `null` means every timeframe. An explicit list confines it. */
  visible_timeframes: Timeframe[] | null;
  overlay_id: string;
  overlay_type: string;
  payload: JsonObject;
  label: string;
  locked: boolean;
  created_at: IsoDateTime;
  updated_at: IsoDateTime;
}

/** An instrument that has saved analysis. */
export interface DrawingChartRef {
  symbol: string;
  drawing_count: number;
}

// ══════════════════════════════════════════════════════════════════════
//  System
// ══════════════════════════════════════════════════════════════════════

export interface CapabilityOut {
  available: boolean;
  module: string;
  error: string | null;
}

/**
 * Process health *and* degraded capabilities.
 *
 * Note what this deliberately does not claim: that the platform is trading.
 * "Running bot" and "trading bot" are different questions.
 */
export interface HealthOut {
  status: 'ok' | 'degraded';
  version: string;
  environment: Environment;
  venue: Venue;
  database: boolean;
  database_error: string | null;
  alerting: boolean;
  alerting_error: string | null;
  exchange_feed_configured: boolean;
  exchange_feed_error: string | null;
  /** `null` = the breaker state could not be read: unknown, never armed. */
  kill_switch_tripped: boolean | null;
  /** Exception type name only (the route is unauthenticated). */
  kill_switch_error: string | null;
  capabilities: Record<string, CapabilityOut>;
  checked_at: IsoDateTime;
}

/** Which world the platform is in. The UI shows this permanently. */
export interface EnvironmentOut {
  environment: Environment;
  venue: Venue;
  is_live: boolean;
  /** Whether one is configured. No credential value ever appears here. */
  credentials_configured: boolean;
  rest_host: string;
  ws_host: string;
  market_rest_host: string;
  market_ws_host: string;
  /** Configured execution environment, not a readiness or reconciliation claim. */
  execution_enabled: boolean;
  database_backend: string;
  data_dir: string;
  max_risk_per_trade_pct: number;
  max_daily_loss_pct: number;
  max_drawdown_pct: number;
  min_stop_distance_fee_multiple: number;
}

/** Durable operator emergency-stop state plus account telemetry. */
export interface KillSwitchOut {
  tripped: boolean;
  breach: string | null;
  trip_reason: string | null;
  tripped_at: IsoDateTime | null;
  can_rearm_after: IsoDateTime | null;
  /** Must be typed back exactly to re-arm. Deliberately hostile to reflex. */
  rearm_confirmation_phrase: string;
  equity: DecimalString;
  peak_equity: DecimalString;
  day_start_equity: DecimalString;
  realised_pnl_today: DecimalString;
  automatic_account_limits: boolean;
  daily_loss_limit_currency: DecimalString | null;
  daily_loss_used_currency: DecimalString;
  daily_loss_remaining_currency: DecimalString | null;
  daily_loss_used_pct: DecimalString | null;
  drawdown_pct: DecimalString;
  drawdown_limit_pct: DecimalString | null;
  drawdown_used_pct: DecimalString | null;
  warnings: string[];
  checked_at: IsoDateTime;
  /** Whether the money fields above are a measurement. Only `fresh` is; for
   *  `missing` (no equity snapshot yet) and `unreadable` (the account money
   *  cannot be computed) they are placeholders and a warning names the cause.
   *  The trip state, reason and re-arm phrase are real in every case. */
  account_inputs: 'fresh' | 'missing' | 'unreadable';
}

export interface RearmRequest {
  /** 20–4000 characters. What changed. Kept forever. */
  reason: string;
  /** Must match `rearm_confirmation_phrase`, typed exactly. */
  confirmation: string;
  reset_drawdown_baseline?: boolean;
  new_peak_equity?: DecimalString | number | null;
}

export interface MonitoringPnl {
  gross_realized_pnl: DecimalString | null;
  unrealized_pnl: DecimalString | null;
  trading_fees: DecimalString | null;
  funding: DecimalString | null;
  rebates: DecimalString | null;
  other_adjustments: DecimalString | null;
  net_pnl: DecimalString | null;
  source: string | null;
  as_of_ms: number | null;
  /** Observation time of unrealized_pnl itself; never a newer fee timestamp. */
  unrealized_as_of_ms: number | null;
  /** Durable projection lineage; the adapter sub-source is not stored on PnlMark. */
  unrealized_source: 'position_current_allocation' | null;
  unrealized_state: 'fresh' | 'stale' | 'unavailable';
}

export interface MonitoringDeploymentRow {
  deployment_id: number;
  public_ref: string;
  name: string;
  strategy_def_id: number;
  /** Shared by the rows one basket deploy created; null for a single deploy. */
  basket_ref?: string | null;
  symbol: string;
  timeframe: Timeframe;
  environment: Environment;
  status: DeploymentStatus;
  desired_status: DeploymentStatus;
  execution_state: ExecutionState;
  /** False when the stored word is outside the API's `ExecutionState` enum (a newer
   *  executor): the row is listed but needs attention, never healthy [S4]. */
  execution_state_known?: boolean;
  executor_heartbeat_at_ms: number | null;
  runner_observation?: RunnerObservation | null;
  last_command_id: string | null;
  last_executor_error: string | null;
  allocated_capital: DecimalString;
  risk_per_trade_pct: DecimalString;
  max_daily_loss_pct: DecimalString | null;
  max_drawdown_pct: DecimalString | null;
  risk_contract_error?: string | null;
  max_open_positions: number;
  trade_count: number;
  open_trade_count: number;
  /** Live/paper execution attribution from this deployment's lot ledger:
   *  setups taken, how many filled vs were skipped (refused before opening),
   *  the fill rate, and the closed win/loss split. `fill_pct`/`win_rate_pct`
   *  are null when the denominator is zero. */
  setups: number;
  filled: number;
  skipped: number;
  fill_pct: number | null;
  wins: number;
  losses: number;
  win_rate_pct: number | null;
  unprotected_positions: number;
  exchange_account_id: number | null;
  protection_state: 'flat' | 'verified' | 'unprotected' | 'unverified';
  reconciliation_status: string;
  reconciliation_as_of_ms: number | null;
  open_risk: DecimalString;
  drawdown_pct: DecimalString | null;
  equity: DecimalString | null;
  peak_equity: DecimalString | null;
  last_activity_ms: number | null;
  /** Per-strategy "is it actually deciding?" verdict from the latest closed-candle
   *  decision. `deciding` = healthy (recent setup/no_setup); `blocked` = the
   *  evaluator refuses every candle (silent no-trade, e.g. a contract mismatch);
   *  `stale` = RUNNING but no recent decision; `stalled` = evaluation never
   *  completed; `warming`/`pending` = starting up; `idle` = not RUNNING. */
  decision_health: DecisionHealth;
  last_decision_status: string | null;
  last_decision_reason: string | null;
  last_decision_at_ms: number | null;
  pnl: MonitoringPnl;
  /** The fleet group this row belongs to (the risk group key). */
  group_ref?: string;
  /** `open_risk` bounds the loss only when a resting stop covers the position:
   *  `unbounded` when any open tuple has no covering stop (M2-S1). */
  open_risk_state: OpenRiskState;
  /** The last command while it is still in flight; null once terminal or absent. */
  pending_command: PendingCommand | null;
  /** The ACTIVE Flatten/Terminate operation refusing this runner's new entries. */
  entry_barrier: EntryBarrier | null;
  /** Paper runners record trades outside the exchange ledger these counts read. */
  ledger_scope: LedgerScope;
}

/** How far `open_risk` bounds the loss (M2-S1). */
export type OpenRiskState = 'none' | 'bounded' | 'unbounded';
export type LedgerScope = 'exchange' | 'paper_untracked';

/** A row's last command while it is persisted, claimed, submitting, acknowledged,
 *  partially filled, cleaning up, verifying or uncertain (M2-S1). */
export interface PendingCommand {
  command_id: string;
  command_type: string;
  status: string;
  requested_at_ms: number;
  claimed_at_ms: number | null;
}

/** The ACTIVE barrier covering a runner (M2-S1, RV-2). */
export interface EntryBarrier {
  kind: string;
  phase: string | null;
  installed_at_ms: number | null;
}

/** The runner lifecycle (`LiveState`, backend/app/lifecycle/live_runner.py). */
export type LiveState =
  | 'constructed' | 'reconciling' | 'reconciled' | 'diverged' | 'stale' | 'running' | 'paused' | 'halted' | 'stopped';

/** The runner's own entry verdict (Monitoring v2 M6-S6); absent on an older executor. */
export interface RunnerEntryGate {
  open: boolean;
  blocked_by: string[];
  reason: string | null;
  evaluated_at_ms: number;
  last_refusal?: { code: string; reason: string; at_ms: number } | null;
}

export type DecisionHealth =
  | 'deciding'
  | 'blocked'
  | 'stale'
  | 'stalled'
  | 'warming'
  | 'pending'
  | 'idle';

/** Live terminate/flatten progress for the strategy view (C1). The step data
 *  comes straight from the durable OperationBarrier + TradingCommand. */
export interface TerminateOperation {
  kind: string | null;
  state: string | null;
  phase: string | null;
  phase_order: string[];
  command_status: string | null;
  reason: string | null;
  installed_at_ms: number | null;
  released_at_ms: number | null;
}

export interface MonitoringDeploymentDetail extends MonitoringDeploymentRow {
  operation: TerminateOperation | null;
  last_command: {
    command_id: string;
    type: string;
    status: string;
    reason: string | null;
    requested_at_ms: number | null;
    completed_at_ms: number | null;
    result_detail: unknown;
  } | null;
  spec_snapshot: Record<string, unknown>;
  params: Record<string, unknown>;
}

export interface MonitoringTrade extends MonitoringPnl {
  trade_id: string;
  exchange_account_id: number;
  environment: Environment;
  economic_side: 'long' | 'short';
  entry_intent_at_ms: number;
  deployment_id: number;
  deployment_ref: string;
  deployment_name: string;
  symbol: string;
  side: 'buy' | 'sell' | null;
  position_side: PositionSide;
  status: string;
  leverage: number | null;
  quantity: DecimalString;
  remaining_quantity: DecimalString;
  average_entry_price: DecimalString;
  average_exit_price: DecimalString | null;
  intended_stop_price: DecimalString;
  original_stop_price: DecimalString;
  target_price: DecimalString | null;
  opened_at_ms: number | null;
  closed_at_ms: number | null;
  exit_reason: string | null;
  protection: Array<{
    exchange_order_id: string | null;
    exchange_algo_id: string | null;
    scope: 'trade' | 'account_symbol_side';
    type: string;
    trigger_price: DecimalString | null;
    quantity: DecimalString | null;
    close_position: boolean;
    status: string;
  }>;
}

export interface MonitoringCoinSide extends MonitoringPnl {
  observed_at_ms?: number | null;
  exchange_account_id: number;
  environment: Environment | null;
  symbol: string;
  position_side: PositionSide;
  economic_side: 'long' | 'short' | 'flat' | null;
  quantity: DecimalString | null;
  ledger_quantity: DecimalString;
  average_entry_price: DecimalString | null;
  mark_price: DecimalString | null;
  liquidation_price: DecimalString | null;
  notional: DecimalString | null;
  deployment_ids: number[];
  deployment_names: string[];
  open_lot_count: number;
  captured_at_ms: number | null;
  position_source: string | null;
  snapshot_state: 'fresh' | 'stale' | 'missing';
}

export interface ExchangeExecution {
  id: number;
  execution_id: string;
  exchange_order_id: string | null;
  exchange_account_id: number;
  symbol: string;
  position_side: string;
  side: string;
  executed_at_ms: number;
  quantity: string;
  price: string;
  quote_notional: string;
  realized_pnl: string | null;
  fees_by_asset: Record<string, string>;
  origin: 'strategy' | 'external';
  deployment_id: number | null;
  deployment_name: string | null;
}

export interface RunnerObservation {
  /** Null only on a malformed stored report (`malformed: true`, M2-S1). */
  observed_at_ms: number | null;
  stale: boolean;
  running: boolean;
  tasks: Record<string, boolean>;
  /** The runner lifecycle state; absent from an executor older than this field. */
  state?: LiveState;
  /** The supervisor paused the runner itself (for example on a feed reconnect). */
  auto_paused?: boolean;
  auto_pause_reason?: string | null;
  /** The stored report was not an object: read as stale, never as healthy (M2-S1). */
  malformed?: boolean;
  /** The runner's own entry verdict (M6-S6); absent until that executor ships. */
  entry_gate?: RunnerEntryGate | null;
  /** Durable terminal cause retained by the runner; null while healthy. */
  halt_reason?: string | null;
  stream_live?: boolean;
  strategy_risk_ready?: boolean;
  strategy_risk_error?: string | null;
  protection_ok?: boolean;
  last_reconcile_converged?: boolean;
  bars_processed?: number;
  last_bar_time?: string | null;
  market_data_watermark?: string | null;
  market_data_snapshots?: string[];
  entries_submitted?: number;
  last_decision?: {
    watermark: string;
    status: 'setup' | 'no_setup' | 'blocked' | 'warming';
    reason: string;
    basket_ready: boolean | null;
    candidate_count: number;
    setup_count: number;
    checks?: Array<{
      label: string;
      passed: boolean;
      observed: string;
      requirement: string;
    }>;
  } | null;
  /** Crash-safe expected -> terminal proof for the latest closed candle. */
  decision_progress?: {
    watermark: string;
    state: 'expected' | 'completed' | 'failed' | 'missing';
    expected_at_ms: number;
    completed_at_ms: number | null;
    reason: string | null;
    decision_status: 'setup' | 'no_setup' | 'blocked' | 'warming' | null;
  } | null;
  evaluation_cadence?: string;
  watchdog_interval_seconds?: number;
}

export interface ExchangeExecutionsOut {
  observed_at_ms: number | null;
  stale: boolean;
  items: ExchangeExecution[];
}

/** One frozen entry gate, its observed value and the requirement it is judged by. */
export interface DecisionGateCheck {
  label: string;
  passed: boolean;
  observed: string;
  requirement: string;
}

/** A setup that was emitted but held in the stale-reconciliation admission queue.
 *  `queued`/`expired`/`voided` = not (yet) sent; `submitted` = eventually sent. */
export interface QueuedEntryOut {
  position_side: string;
  status: 'queued' | 'submitted' | 'expired' | 'voided';
  resolution_reason: string | null;
  queued_at_ms: number;
  resolved_at_ms: number | null;
}

/** One market's closed-candle decision at a single watermark. `state` is the
 *  durable expected -> terminal proof: a non-`completed` state is an interrupted
 *  evaluation, never a `no_setup` result. */
export interface MarketDecision {
  state: 'expected' | 'completed' | 'failed' | 'missing';
  status: 'setup' | 'no_setup' | 'blocked' | 'warming' | null;
  reason: string | null;
  basket_ready: boolean | null;
  candidate_count: number | null;
  setup_count: number | null;
  checks: DecisionGateCheck[];
  expected_at_ms: number | null;
  completed_at_ms: number | null;
  queued: QueuedEntryOut[];
}

/** Every market's decision at one closed candle, keyed by `String(deployment_id)`. */
export interface DecisionCandle {
  watermark_ms: number;
  candle_open_ms: number;
  candle_close_ms: number | null;
  decisions: Record<string, MarketDecision | undefined>;
}

/** The last-N-hours decision journal for one strategy's markets. */
export interface GroupDecisionsOut {
  generated_at_ms: number;
  hours: number;
  timeframe: string | null;
  markets: Array<{ deployment_id: number; symbol: string; timeframe: string }>;
  candles: DecisionCandle[];
}

export interface MonitoringRunnerCounts {
  /** Market runners that are not terminated. */
  total: number;
  /** Terminated (CLOSED) markets: never runners, never in `total` (M2-S1). */
  terminated: number;
  attached: number;
  attaching: number;
  pending: number;
  failed: number;
  stale: number;
  detached: number;
  blocked_live: number;
  legacy_review: number;
}

export interface MonitoringGroup {
  group_ref: string;
  deployment_ids: number[];
  name: string;
  strategy_def_id: number;
  basket_ref: string | null;
  environment: Environment;
  exchange_account_id: number | null;
  symbols: string[];
  timeframe: Timeframe;
  allocated_capital: DecimalString | null;
  allocation_consistent: boolean;
  equity: DecimalString | null;
  open_risk: DecimalString | null;
  trade_count: number;
  open_trade_count: number;
  unprotected_positions: number;
  runner_counts: MonitoringRunnerCounts;
  reconciliation_status: string;
  /** THE active-strategy predicate (a member not CLOSED, or holding an open lot). */
  active: boolean;
  open_risk_state: OpenRiskState;
  pnl: MonitoringPnl;
  /** Long vs short P&L, aggregated from the same per-lot figures as the Trades
   *  tab — long + short reconciles to the individual-trade P&L. */
  pnl_by_side: { long: MonitoringPnl; short: MonitoringPnl };
  /** Per-market Long vs short P&L, keyed by `String(deployment_id)`. Same per-lot
   *  figures as the Trades tab, so each market's long + short reconciles to that
   *  market's individual-trade P&L. */
  pnl_by_side_by_deployment: Record<string, { long: MonitoringPnl; short: MonitoringPnl }>;
}

export interface MonitoringGroupDetail {
  group: MonitoringGroup;
  deployments: DeploymentOut[];
  trades: MonitoringTrade[];
  open_trades: MonitoringTrade[];
  positions: PositionOut[];
  executions: ExchangeExecutionsOut;
  limits: MonitoringLimit | null;
  equity: EquityPointOut[];
  equity_state: 'allocation_mismatch' | 'awaiting_synchronized_marks' | 'retained_synchronized_marks';
  activity: Array<LogLineOut & { event_id: string; deployment_id: number; symbol: string }>;
  generated_at_ms: number;
  trade_limit: number;
  execution_limit: number;
  activity_limit: number;
}

export interface MonitoringEnvironmentTotal {
  environment: Environment;
  strategy_count: number;
  allocated_capital: DecimalString | null;
  equity: DecimalString | null;
  open_risk: DecimalString | null;
  open_trade_count: number;
  trade_count: number;
  unprotected_positions: number;
  open_risk_state: OpenRiskState;
  runner_counts: MonitoringRunnerCounts;
  /** Who owns this environment's money: the Portfolio book for TESTNET/LIVE;
   *  paper money is not in the exchange ledger (`not_measured`). */
  money_source: 'portfolio_book' | 'not_measured';
  /** Always null from the server: no total carries a second money number. */
  pnl: MonitoringPnl | null;
}

/** `GET /monitoring/overview`: one response feeds the fleet, the ribbon counts
 *  and the basket card (M2-S1, S5). `rows` are exactly the deployment rows of
 *  the same `include_closed`; totals and `runner_counts` cover active groups. */
export interface MonitoringOverview {
  generated_at_ms: number;
  currency: 'USDT';
  groups: MonitoringGroup[];
  rows: MonitoringDeploymentRow[];
  totals_by_environment: MonitoringEnvironmentTotal[];
  runner_counts: MonitoringRunnerCounts;
}

export interface MonitoringLimit {
  deployment_id: number;
  deployment_ids: number[];
  deployment_ref: string;
  basket_ref: string | null;
  name: string;
  symbol: string;
  markets: string[];
  environment: Environment;
  status: DeploymentStatus;
  allocated_capital: DecimalString;
  equity: DecimalString | null;
  peak_equity: DecimalString | null;
  drawdown_pct: DecimalString | null;
  drawdown_limit_pct: DecimalString | null;
  drawdown_remaining_pct: DecimalString | null;
  realized_today: DecimalString | null;
  daily_loss_limit: DecimalString | null;
  daily_loss_enabled?: boolean;
  measurement_state?: string;
  error?: string | null;
  daily_loss_used: DecimalString | null;
  daily_loss_remaining: DecimalString | null;
  open_risk: DecimalString;
  open_positions: number;
  max_open_positions: number;
  as_of: IsoDateTime | null;
}

/** Stop everything, now. The one action this platform never argues with. */
export interface ManualHaltRequest {
  reason: string;
}

/**
 * Gaps, duplicates and impossible bars.
 *
 * Gaps are reported rather than treated as failures — exchanges genuinely
 * halt. Duplicates, NaNs and impossible OHLC are failures, because no
 * market condition produces them.
 */
export interface DataQualityOut {
  symbol: string;
  timeframe: Timeframe;
  rows: number;
  start: IsoDateTime | null;
  end: IsoDateTime | null;
  ok: boolean;
  is_continuous: boolean;
  completeness_pct: number;
  duplicate_count: number;
  missing_bars: number;
  ohlc_violation_count: number;
  nan_total: number;
  gaps: JsonObject[];
  summary: string;
}

export interface DataQualityOverviewRow {
  symbol: string;
  timeframe: Timeframe;
  rows?: number;
  start?: IsoDateTime | null;
  end?: IsoDateTime | null;
  bytes_on_disk?: number;
  [key: string]: unknown;
}

export interface AuditEntryOut {
  id: number;
  action: string;
  actor: string;
  reason: string;
  entity_type: string | null;
  entity_id: string | null;
  before: JsonObject | null;
  after: JsonObject | null;
  created_at: IsoDateTime;
}

export interface AuditQuery {
  entity_type?: string;
  entity_id?: string;
  actor?: string;
  action?: string;
  start?: IsoDateTime;
  end?: IsoDateTime;
  limit?: number;
}

export interface VersionOut {
  name: string;
  version: string;
  api: string;
  python: string;
}

export interface UptimeOut {
  started_at?: IsoDateTime;
  uptime_seconds?: number;
  [key: string]: unknown;
}

// ══════════════════════════════════════════════════════════════════════
//  WebSocket frames
// ══════════════════════════════════════════════════════════════════════

export const WS_TOPIC_JOBS = 'jobs';
export const WS_TOPIC_ALERTS = 'alerts';
export const WS_TOPIC_POSITIONS = 'positions';
export const WS_TOPIC_DEPLOYMENTS = 'deployments';
export const WS_CANDLE_PREFIX = 'candles';

export type WsStaticTopic =
  | typeof WS_TOPIC_JOBS
  | typeof WS_TOPIC_ALERTS
  | typeof WS_TOPIC_POSITIONS
  | typeof WS_TOPIC_DEPLOYMENTS;

/** `candles:BTCUSDT:1m` */
export type WsCandleTopic = `candles:${string}:${Timeframe}`;
export type WsTopic = WsStaticTopic | WsCandleTopic;

export function candleTopic(symbol: string, timeframe: Timeframe): WsCandleTopic {
  return `${WS_CANDLE_PREFIX}:${symbol.toUpperCase()}:${timeframe}`;
}

export interface WsWelcomeFrame {
  type: 'welcome';
  ts: IsoDateTime;
  heartbeat_seconds: number;
  topics: string[];
  errors: string[];
  available_topics: string[];
}

export interface WsSubscribedFrame {
  type: 'subscribed' | 'unsubscribed';
  topics: string[];
  errors: string[];
  active: string[];
  ts: IsoDateTime;
}

/** A non-zero `dropped` means this client is not keeping up and has lost
 *  the oldest messages — which is the correct failure for market data. */
export interface WsHeartbeatFrame {
  type: 'heartbeat';
  ts: IsoDateTime;
  dropped: number;
  topics: string[];
}

export interface WsPongFrame {
  type: 'pong';
  ts: IsoDateTime;
}

export interface WsErrorFrame {
  type: 'error';
  message: string;
  ts: IsoDateTime;
}

export interface WsEventFrame<T = unknown> {
  type: 'event';
  topic: string;
  ts: IsoDateTime;
  data: T;
}

export type WsFrame =
  | WsWelcomeFrame
  | WsSubscribedFrame
  | WsHeartbeatFrame
  | WsPongFrame
  | WsErrorFrame
  | WsEventFrame;

/** `candles:*` payload. `closed` false means the bar is still forming —
 *  the chart wants every update, a strategy must act only on closed bars. */
export interface WsCandleEvent {
  symbol: string;
  timeframe: Timeframe;
  t: EpochSeconds;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  closed: boolean;
  /** Present instead of the OHLC fields when the upstream stream died. */
  error?: string;
  stream?: 'stopped';
}

export interface WsPositionSnapshot {
  position_id: string;
  deployment_id: number | null;
  symbol: string;
  side: Side;
  quantity: DecimalString;
  entry_price: DecimalString;
  stop_price: DecimalString;
  protected_quantity: DecimalString;
  is_fully_protected: boolean;
  opened_at: IsoDateTime;
}

export interface WsPositionsEvent {
  positions: WsPositionSnapshot[];
  open: number;
  unprotected: number;
  exchange_marks: Array<{
    exchange_account_id: number;
    symbol: string;
    position_side: PositionSide;
    quantity: DecimalString;
    mark_price: DecimalString | null;
    unrealized_pnl: DecimalString;
    source: string;
    observed_at_ms: number;
    exchange_updated_at_ms: number | null;
  }>;
}

export interface WsAlertEvent {
  id: number;
  severity: AlertSeverity;
  category: string;
  title: string;
  message: string;
  deployment_id: number | null;
  created_at: IsoDateTime;
}

export interface WsDeploymentChange {
  id: number;
  name: string;
  symbol: string;
  status: DeploymentStatus;
  halt_reason: string | null;
  environment: Environment;
}

export interface WsDeploymentsEvent {
  deployments: WsDeploymentChange[];
}

/** The `jobs` topic republishes a `JobOut` snapshot on every progress tick. */
export type WsJobEvent = JobOut;

// ══════════════════════════════════════════════════════════════════════
//  Database — the browsable record
// ══════════════════════════════════════════════════════════════════════

/**
 * The Database tab's contract.
 *
 * One rule governs every type below: **the frontend holds no second copy of
 * the whitelist.** Which tables exist, which columns the list projects, which
 * may be filtered and with which operators, whether a row may be deleted and
 * the sentence explaining why not — all of it is served by
 * `GET /api/db/tables` and none of it is re-declared here. A hardcoded copy
 * is a control whose state can drift from the rule the server actually
 * applies, which is M4 in its purest form: a delete button that is enabled
 * because the frontend believes the table is deletable, over a server that
 * refuses.
 *
 * The refusal copy in particular is the server's prose, rendered verbatim.
 * The same string reaches the screen three ways — the browser badge's
 * `title`, the detail modal's AlertCard, and the 409's `message` — and there
 * is one origin for all three.
 */

/** What a column holds, which decides its formatter and its operators. */
export type DbColumnKind =
  | 'int'
  | 'float'
  | 'bool'
  | 'text'
  | 'datetime'
  | 'money'
  | 'json';

/** Filter operators. `isnull`/`notnull` take no value. */
export type DbFilterOp =
  | 'eq'
  | 'ne'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'contains'
  | 'isnull'
  | 'notnull';

export interface DbColumnMeta {
  name: string;
  kind: DbColumnKind;
  nullable: boolean;
  /**
   * Present in the list projection. `false` for every JSON/free-text column:
   * the list never touches them, not even for a byte length — `length()` on
   * `backtest_runs.equity_curve` measured 206 ms because it must read the
   * value through 68.6 MB of overflow pages.
   */
  in_list: boolean;
  sortable: boolean;
  filterable: boolean;
  /** The operators this column's kind permits. Empty for `json`. */
  ops: DbFilterOp[];
  /** Included in the free-text `q` scan. */
  searchable: boolean;
  /** Content this platform did not author. Scrubbed before it leaves Python. */
  redacted: boolean;
}

export interface DbTableSummary {
  name: string;
  row_count: number;
  column_count: number;
  /** Not always `id`: `alert_log` keys on TEXT `alert_id`, `alembic_version`
   *  on VARCHAR `version_num`. A route typed `int` breaks on 2 of 13. */
  primary_key: string;
  primary_key_type: 'int' | 'str';
  deletable: boolean;
  /** A finished sentence, written for a person. Rendered verbatim. */
  refusal_reason: string | null;
  /** The empty-state copy: what the table records and what writes it. */
  description: string;
  columns: DbColumnMeta[];
}

export interface DbTableListOut {
  /** Read from `alembic_version.version_num`, never a constant. */
  schema_version: string;
  generated_at: IsoDateTime;
  tables: DbTableSummary[];
}

/** One list row: only `in_list` columns, already decoded. */
export type DbRow = Record<string, string | number | boolean | null>;

export interface DbRowPageOut {
  /**
   * Echoed on purpose. It is what lets `useDbRows` write `placeholderData` in
   * the predicate form — `previous?.table === table ? previous : undefined` —
   * instead of the unqualified `keepPreviousData`, which would draw table A's
   * rows under table B's header for a frame.
   */
  table: string;
  items: DbRow[];
  /** Before limit/offset, so the pager is real. */
  total: number;
  limit: number;
  offset: number;
  sort: string;
  order: 'asc' | 'desc';
}

/**
 * A large column, bounded by construction.
 *
 * `content` is the whole value up to 64 KB; above that it is `null`,
 * `truncated` is true and `preview` carries the first 4 KB, scrubbed.
 */
export interface DbBlobOut {
  bytes: number;
  content: string | null;
  truncated: boolean;
  preview: string | null;
  redacted: boolean;
}

/** A foreign key pointing AT this row, counted before any delete is tried. */
export interface DbDependentOut {
  table: string;
  column: string;
  count: number;
  on_delete: 'cascade' | 'restrict';
  /** True when the referencing table is itself append-only. */
  protected: boolean;
}

export interface DbRowDetailOut {
  table: string;
  primary_key: string;
  row: Record<string, unknown>;
  blobs: Record<string, DbBlobOut>;
  dependents: DbDependentOut[];
  /** Fail-closed: false whenever a protected dependent has count > 0. */
  deletable: boolean;
  refusal_reason: string | null;
}

export interface DbRowDeletedOut {
  table: string;
  primary_key: string;
  /** The receipt. The deletion is itself an append-only record. */
  audit_id: number;
}

/** Everything that can change the answer, and nothing that cannot. */
export interface DbRowQuery {
  q?: string;
  /** Wire form `column:op:value`, at most 6. Sorted, so ordering never churns
   *  the query key. */
  filter?: string[];
  sort?: string;
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

// ══════════════════════════════════════════════════════════════════════
//  Strategies redesign & live monitoring — the net-new §7 surfaces
//
//  These are the wire SHAPES the spec (docs/specs/strategies-and-monitoring-
//  redesign.md §7) commits the backend to. Several of the endpoints do not
//  exist yet — they arrive with the "futures executor". Every hook that reads
//  one of them tolerates a 404 and returns `null` so the panel can render an
//  explicit "awaiting futures executor" pending state instead of an error, and
//  light up unchanged the moment the endpoint lands. No field here is ever
//  synthesised in the frontend: a number that does not exist on the wire is a
//  pending state, never an invented value (E2, and OWNER DECISION 2).
//
//  Money note: the spec's wireframes write "€" as shorthand for "money on the
//  account". This app has ONE money renderer (`lib/format.ts::formatMoney`)
//  and it forbids a currency glyph, rendering the quote asset instead
//  (`… USDT`). The wire field names below keep the spec's documented spelling
//  (`profit_eur`, `max_drawdown_eur`) so the backend contract matches §7
//  verbatim; the VALUE is a `DecimalString` rendered through `formatMoney`,
//  never with a `€`.
// ══════════════════════════════════════════════════════════════════════

/** Exchange position-side field. Production execution is one-way/BOTH; LONG
 *  and SHORT remain readable only for imported/diagnostic exchange facts. */
export type PositionSide = 'both' | 'long' | 'short';

/**
 * §7-A. One row of the per-VARIANT registry aggregate,
 * `GET /api/strategies/registry`.
 *
 * One row per REGISTERED variant — a distinct (exit model, capital, leverage,
 * risk, compounding), each with its OWN evidence run's money, never the family
 * def's latest run (the collapse that once headlined an S2 family with an €8.9M
 * SDK-path artefact). `backtested` is the Gate A1 flag. Every money field is a
 * `DecimalString` and null when there is no run to measure — "unknown", which
 * the table renders as an em dash, never as zero. `stage` is NOT here: it is a
 * def-level lifecycle property the tab already fetches via `useLifecycleVersions`
 * and joins by `strategy_def_id`.
 */
export interface RegistryPreparationState {
  state: 'pending' | 'preparing' | 'ready' | 'failed' | 'unavailable';
  prepared_markets: string[];
  total_markets?: number;
  executor_id?: string;
  fingerprint?: string;
  environment?: string;
  prepared_at_ms?: number;
  exchange_verified: false;
  error?: string | null;
}

export interface StrategyRegistryRow {
  strategy_def_id: number;
  /** The `strategy_variants` row id — the selection/delete key. Null for a
   *  legacy orphan-deployment row that predates the variant table. */
  variant_id: number | null;
  runtime_contract?: { executor_preparation?: RegistryPreparationState } | null;
  /** Display family this variant groups under, e.g. "S2 Safe". */
  name: string;
  family_code: string;
  version: number;
  kind: StrategyKind;
  /** Identity of the sizing envelope + exit model; deployed variants sharing
   *  it are grouped into one line in the registry. */
  variant_key: string;
  exit_model: string;
  allocated_capital: DecimalString;
  leverage: number;
  risk_per_trade_pct: DecimalString;
  compounding_mode: string;
  /** Gate A1: true iff a completed backtest run with trades justifies it. */
  backtested: boolean;
  /** The run whose money headline this row shows, and that a deploy cites. */
  backtest_run_id: number | null;
  /** final_equity − initial_capital of this variant's evidence run. */
  profit_eur: DecimalString | null;
  win_rate_pct: number | null;
  profit_factor: number | null;
  /** The true peak-to-trough curve drawdown, as a positive percent. */
  max_drawdown_pct: number | null;
  /** Highest simultaneously open strategy-lot count measured by this run. */
  peak_concurrent?: number | null;
  /** Peak-relative money drawdown; null (em dash) — the run does not store the
   *  peak equity, and the initial-capital approximation understated ruin. */
  max_drawdown_eur: DecimalString | null;
  total_trades: number | null;
  period_start: IsoDateTime | null;
  /** `halted_at ?? period_end` — the last bar actually reached. */
  evaluated_end: IsoDateTime | null;
  /** True when at least one deployment cites this variant. */
  deployed: boolean;
  deployment_count: number;
  /** When this variant was moved to the registry. Null for an orphan row. */
  registered_at: IsoDateTime | null;
  /** When it was first deployed. Null when not deployed. */
  deployed_at: IsoDateTime | null;
  /** The owner-legible unique id: `REG-0001` for a registered variant; a
   *  legacy orphan row carries its deployment's `DEP-…` public ref. */
  registry_ref: string;
  /** Where it stands, from facts: BACKTEST (evidence, not deployed), PAPER
   *  (deployed on fake money — Binance testnet), LIVE (real money), ISSUE (the
   *  evidence is missing, failed, halted or empty — `stage_detail` says why). */
  stage: RegistryStage;
  stage_detail: string | null;
  /** Distinct environments of the deployments citing this row. */
  environments: Environment[];
  /** Whether this variant can actually be DEPLOYED — the same question gate L1
   *  asks at the deploy click, answered here so it is known at registry time.
   *  False for an exit model that exists only in the backtest resolver:
   *  deploying it would attach a runner trading the WRONG exit. */
  live_ready: boolean;
  /** One sentence naming what is missing. Null when `live_ready`. */
  live_blocker: string | null;
  /** In the Deleted folder — the registry's Active/Deleted view partitions on it. */
  is_archived: boolean;
}

/** The registry's four stages — the owner's vocabulary, not the lifecycle ladder. */
export type RegistryStage = 'backtest' | 'paper' | 'live' | 'issue';

/** §7-A envelope. `stale` withholds the rows rather than showing an old
 *  picture (spec §0: stale = withheld, not shown). */
export interface StrategyRegistryOut {
  as_of: IsoDateTime;
  stale: boolean;
  rows: StrategyRegistryRow[];
}

/**
 * §7-F/G/H/I. One consolidated `(coin, side)` position — the real account-risk
 * unit, `GET /api/portfolio/consolidated`.
 *
 * Net quantity, the shared liquidation price and gross notional are the
 * exchange's truth (EX); lot and strategy attribution is the app ledger (APP).
 * This block exists in no surveyed tool and is where strategies stack into one
 * shared liquidation.
 */
export interface ConsolidatedPosition {
  symbol: string;
  position_side: PositionSide;
  /** Signed net quantity the exchange holds for this (coin, side). */
  net_qty: DecimalString;
  /** Size-weighted average entry across every lot in this (coin, side). */
  avg_entry: DecimalString;
  mark_price: DecimalString | null;
  unrealised_pnl: DecimalString | null;
  /** §7-G. The shared liquidation price, read from the exchange. Null until
   *  the futures executor reports it — never the backtest planning figure. */
  liquidation_price: DecimalString | null;
  /** Percent distance from mark to the shared liquidation. */
  liquidation_distance_pct: number | null;
  /** §7-I. Gross notional this (coin, side) contributes. */
  gross_notional: DecimalString | null;
  /** How many individual lots (strategy opens) net into this position. */
  lot_count: number;
  /** How many distinct strategies are stacked here. */
  strategy_count: number;
  /** Names of the strategies stacked into this (coin, side). */
  strategies: string[];
  /** Quantity with no resting stop on the exchange. Above zero is an emergency. */
  unprotected_qty: DecimalString;
}

/**
 * §7-F/I. `GET /api/portfolio/consolidated`.
 *
 * Carries the account-wide gross-vs-cap gauge (§7-I, the ruin driver) and the
 * margin/liquidation trio, all from the exchange.
 */
export interface ConsolidatedPositionsOut {
  as_of: IsoDateTime;
  /** Stale = withheld + the money action refused (spec §0/§4). */
  stale: boolean;
  positions: ConsolidatedPosition[];
  /** §7-I. Account-wide gross notional and its configured ceiling. */
  gross_notional: DecimalString | null;
  gross_cap: DecimalString | null;
  /** gross_notional / equity, the multiple the gauge shows. */
  gross_ratio: number | null;
  gross_cap_ratio: number | null;
  /** IBKR trio — "how close to liquidation" (§4.2). */
  net_equity: DecimalString | null;
  maintenance_margin: DecimalString | null;
  excess_liquidity: DecimalString | null;
}

/** One resting protective order on the exchange (§7-K). */
export type RestingOrderKind = 'stop' | 'take_profit';
export type RestingOrderState =
  | 'resting'
  | 'filled'
  | 'rejected'
  | 'cancelled'
  | 'unknown';

/**
 * §7-K. The resting stop/target detail for one lot,
 * `GET /api/portfolio/positions/{id}/orders`.
 *
 * Today `PositionOut` carries only the stop's `protective_order_ids` and a
 * `take_profit_price` with no order id and no state. This adds the take-profit
 * order id and the live on-exchange state of every protective order.
 */
export interface RestingOrder {
  order_id: string;
  kind: RestingOrderKind;
  price: DecimalString;
  state: RestingOrderState;
  /** True while the order is actually resting on the venue (fires if we die). */
  on_exchange: boolean;
}

export interface PositionOrdersOut {
  position_id: string;
  orders: RestingOrder[];
}

/**
 * §7-C / §5. The deployment record — the `Deployment` row PLUS the fields that
 * make it an auditable proof of what was deployed and on what evidence.
 *
 * These are optional on {@link DeploymentOut} because the backend that writes
 * them is §7-C work: an older serialization simply omits them, and the UI
 * treats absent as "record not written yet".
 */
export interface DeploymentRecordFields {
  /** Gate A1: true iff a completed run justified the deploy. */
  backtested?: boolean;
  /** The exact run that justified the deploy — for reconciliation and audit. */
  backtest_run_id?: number | null;
  /** Frozen full strategy spec at deploy: kind, code_ref/rule_spec, params,
   *  required timeframes/indicators, cost model, symbols/basket. */
  spec_snapshot?: JsonObject | null;
  /** The exact preflight scorecard at deploy, incl. any relaxed/overridden
   *  criteria with their reasons (Gate D4). */
  gate_scorecard_snapshot?: JsonObject | null;
}

/**
 * §5. The body written on deploy to attach the record fields to a freshly
 * created (STOPPED) deployment. A separate write so a create against a backend
 * that predates §7-C still succeeds; the record write then 404s and the UI
 * says the record is pending.
 */
export interface DeploymentRecordWrite {
  backtested: boolean;
  backtest_run_id: number | null;
  spec_snapshot: JsonObject;
  gate_scorecard_snapshot: JsonObject;
}

/**
 * §7-E. The deploy preflight, `POST /api/lifecycle/{id}/preflight`.
 *
 * Composes the existing gate scorecard with the exchange-facing gates E1/E2/F1
 * (reconcile, venue filters + hedge mode + key scope, shared liquidation and
 * gross cap). Returns the SAME shape as the gate scorecard so one renderer
 * draws both. Until the endpoint exists the frontend assembles what it can
 * from existing wire fields and marks the exchange gates pending.
 */
export interface PreflightRequest {
  target_stage: LifecycleStage;
  backtest_run_id?: number | null;
  /** The sized book the preflight is evaluated against. */
  environment: Environment;
  symbol: string;
  allocated_capital: DecimalString | number;
  risk_per_trade_pct?: DecimalString | number;
  max_open_positions?: number;
}

export type PreflightOut = ScorecardOut;

// ══════════════════════════════════════════════════════════════════════
//  Portfolio v2 — the exchange-ledger book (/api/portfolio/book*)
//  Spec: docs/specs/portfolio-dashboard-v2.md. Money is a decimal string.
// ══════════════════════════════════════════════════════════════════════

export type BookEnvironment = 'testnet' | 'live';
export type BookGranularity = 'day' | 'week' | 'month';
export type MarkState = 'fresh' | 'stale' | 'unavailable';

/** The five realized components and their net, exact. */
export interface BookRealized {
  gross_realized_pnl: DecimalString;
  trading_fees: DecimalString;
  funding: DecimalString;
  rebates: DecimalString;
  other_adjustments: DecimalString;
  net_pnl: DecimalString;
}

/** Composed P&L for a scope. `null` money is UNKNOWN, never zero. */
export interface BookPnl {
  gross_realized_pnl: DecimalString | null;
  trading_fees: DecimalString | null;
  funding: DecimalString | null;
  rebates: DecimalString | null;
  other_adjustments: DecimalString | null;
  /** Realized net only (after fees and funding). */
  realized_net: DecimalString | null;
  /** Open P&L; withheld (null) when the exchange mark is older than 15 s. */
  unrealized_pnl: DecimalString | null;
  /** Realized net + open P&L; null when open P&L is withheld. */
  net_pnl: DecimalString | null;
  unrealized_state: MarkState | null;
  as_of_ms: number | null;
  unrealized_as_of_ms: number | null;
  source: string | null;
}

export interface BookTradeStats {
  /** Closed trades the ledger has priced — every statistic below uses these. */
  closed: number;
  /** Closed lots with no ledger P&L yet; never counted as breakeven. */
  unmeasured: number;
  wins: number;
  losses: number;
  breakeven: number;
  win_rate_pct: DecimalString | null;
  profit_factor: DecimalString | null;
  profit_factor_reason: string | null;
  gross_profit: DecimalString;
  gross_loss: DecimalString;
  net: DecimalString;
  expectancy: DecimalString | null;
  avg_win: DecimalString | null;
  avg_loss: DecimalString | null;
  best: DecimalString | null;
  worst: DecimalString | null;
  truncated?: boolean;
}

export interface BookEquityNow {
  value: DecimalString | null;
  state: MarkState;
  as_of_ms: number | null;
  reason: string | null;
}

export interface BookDrawdownEpisode {
  peak: DecimalString;
  trough: DecimalString;
  depth_pct: DecimalString;
  peak_at_ms: number;
  trough_at_ms: number;
  recovered_at_ms: number | null;
  underwater_ms: number;
}

export interface BookDrawdown {
  state: 'ok' | 'unavailable';
  reason: string | null;
  high_water: DecimalString | null;
  current_drawdown_pct: DecimalString | null;
  /** Why the current drawdown is withheld (stale or missing current equity). */
  current_reason: string | null;
  max_drawdown_pct: DecimalString | null;
  longest_underwater_ms: number | null;
  episodes: BookDrawdownEpisode[];
}

export interface BookLeg {
  deployment_id: number;
  public_ref: string;
  symbol: string;
  timeframe: string;
  status: DeploymentStatus;
  traded: boolean;
  pnl: BookPnl;
  trades: BookTradeStats;
}

export interface BookStrategy {
  key: string;
  name: string;
  is_basket: boolean;
  strategy_def_id: number;
  status: DeploymentStatus;
  active: boolean;
  markets: string[];
  pnl: BookPnl;
  trades: BookTradeStats;
  legs: BookLeg[];
}

export interface BookWindow extends BookRealized {
  start_ms: number;
}

export interface BookOverview {
  environment: BookEnvironment;
  generated_at_ms: number;
  accounts: Array<{ id: number; ref: string; exchange: string }>;
  state: 'ok' | 'no_account';
  reason: string | null;
  equity: BookEquityNow;
  drawdown: BookDrawdown;
  pnl: BookPnl | null;
  today: BookWindow | null;
  month_to_date: BookWindow | null;
  trades: BookTradeStats;
  exit_reasons: BookExitReason[];
  strategies: BookStrategy[];
  unattributed: { pnl: BookRealized & { realized_net: DecimalString } } | null;
  data_quality: { unconverted_fee_events: number; warnings: string[] };
}

export interface BookBucket extends BookRealized {
  start_ms: number;
  end_ms: number;
  label: string;
  /** The current, still-open bucket ("to date"). */
  partial: boolean;
  cumulative_net_pnl: DecimalString;
  trades_closed: number;
}

export interface BookPeriods {
  environment: BookEnvironment;
  state: 'ok' | 'no_account';
  reason: string | null;
  granularity: BookGranularity;
  scope: string;
  scope_label: string;
  start_ms: number;
  end_ms: number;
  baseline: BookRealized;
  total: BookRealized;
  lifetime: BookRealized;
  buckets: BookBucket[];
  trades_truncated: boolean;
  generated_at_ms: number;
}

export interface BookEquityPoint {
  t: number;
  equity: DecimalString;
  low: DecimalString;
  high: DecimalString;
  high_water: DecimalString;
  drawdown_pct: DecimalString | null;
}

export interface BookEquity {
  environment: BookEnvironment;
  generated_at_ms: number;
  equity: BookEquityNow;
  state: 'ok' | 'empty' | 'unavailable';
  reason: string | null;
  start_ms?: number;
  end_ms?: number;
  stored_rows_in_range?: number;
  resolution: 'exact' | 'bucketed' | null;
  points: BookEquityPoint[];
  drawdown: BookDrawdown;
}

export interface BookExitReason {
  reason: string;
  label: string;
  count: number;
  share_pct: DecimalString;
  net: DecimalString;
  average: DecimalString | null;
  wins: number;
  losses: number;
}

export interface BookStops {
  closed_trades: number;
  stop_exits: number;
  losing_trades: number;
  losses_within_planned_risk: number;
  losses_within_planned_risk_pct: DecimalString | null;
  average_planned_risk: DecimalString | null;
  average_loss: DecimalString | null;
  total_stop_overshoot: DecimalString;
  worst_stop_overshoot: DecimalString | null;
}

export interface BookLot {
  id: string;
  strategy_key: string;
  deployment_id: number;
  symbol: string;
  /** From the sizing stop; 'unknown' when a one-way lot has no usable prices. */
  side: 'long' | 'short' | 'unknown';
  opened_at_ms: number | null;
  closed_at_ms: number;
  quantity: DecimalString;
  entry_price: DecimalString;
  exit_price: DecimalString | null;
  /** The stop the size was derived from (planned risk is measured against it). */
  stop_price: DecimalString;
  intended_stop_price: DecimalString;
  planned_risk: DecimalString;
  net: DecimalString;
  /** The ledger's word (`stop_loss` …) — what stored aggregates are keyed on. */
  exit_reason: string | null;
  stop_overshoot: DecimalString | null;
  // The shared stop-and-exit cells' input, derived server-side by the one
  // `read_stop_account` [F6]; null booleans = direction unknown, claim nothing.
  final_stop_price: DecimalString;
  effective_stop_price: DecimalString | null;
  stop_was_moved: boolean | null;
  stop_moved_to_breakeven: boolean | null;
  exited_at_breakeven: boolean | null;
  /** `exit_stop_breakeven` for a stop moved to entry or better, else the plain exit. */
  exit_reason_detail: string;
}

export type BookAttributionDimension = 'strategy' | 'symbol' | 'side' | 'exit_reason';

export interface BookTrades {
  environment: BookEnvironment;
  scope: string;
  scope_label: string;
  truncated: boolean;
  stats: BookTradeStats;
  exit_reasons: BookExitReason[];
  attribution: Record<BookAttributionDimension, AttributionReport>;
  stops: BookStops;
  lots: BookLot[];
}

export interface BookExcursion extends ExcursionReport {
  environment: BookEnvironment;
  scope: string;
  scope_label: string;
  timeframe: string;
  truncated: boolean;
}

export interface MarketDataStatus {
  generated_at_ms: number;
  timeframe: string;
  symbols: Array<{ symbol: string; last_open_ms: number | null; age_ms: number | null; state: 'stored' | 'missing' }>;
  missing_symbols: string[];
  oldest_age_ms: number | null;
  newest_age_ms: number | null;
  topup: {
    started_at_ms?: number;
    finished_at_ms?: number | null;
    ok?: boolean;
    mode?: string;
    symbols?: number;
    new_bars?: number;
    error?: string | null;
  } | null;
  topup_reason: string | null;
  build: {
    finished_at_ms?: number;
    m4_holds?: boolean;
    symbols?: number;
    cells?: number;
    empty_cell_count?: number;
  } | null;
  build_reason: string | null;
}
